import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import * as THREE from 'three';
import html2canvas from 'html2canvas';
import {
  Layers, Save, Image as ImageIcon, Plus, ArrowUp, ArrowDown, Maximize,
  Trash2, Box, MousePointer2, FlipHorizontal, BringToFront, SendToBack,
  Search, RefreshCw, Eye, PlayCircle, Edit2, FileText, ChevronLeft,
  Package, Scissors, Palette, Layout, ClipboardList, ArrowRight,
  PenTool, Type, Move, ZoomIn, ZoomOut, RotateCcw, Download, X
} from 'lucide-react';
import * as XLSX from 'xlsx';

// -----------------------------------------------------------------------------
//  API CONSTANTS
// -----------------------------------------------------------------------------
const API_BASE = 'https://nirvikalp.integrashion.com/api';

// Strip base64 data URIs from an object recursively - keeps POST payload small
const stripBase64 = (obj, depth = 5) => {
  if (depth === 0 || obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(i => stripBase64(i, depth - 1));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.startsWith('data:') && v.length > 500)
      out[k] = '[IMG]'; // strip - images stored separately or in local state only
    else if (typeof v === 'object')
      out[k] = stripBase64(v, depth - 1);
    else
      out[k] = v;
  }
  return out;
};

// The ONLY working endpoint - mirrors TechnicalMasters.js pattern exactly
// ─────────────────────────────────────────────────────────────────────────────
// STYLE FIELD HELPERS — normalise field names across different module saves
// The CREATE STYLE form may save product as 'product', 'productType',
// 'garmentType', 'category', or 'garmentCategory'. Same for fabric.
// ─────────────────────────────────────────────────────────────────────────────
const getStyleProduct = (s) =>
  s.productType || s.product || s.garmentType || s.garmentCategory ||
  s.category || s.itemType || s.type || '';

const getStyleFabric = (s) =>
  s.fabricType || s.fabric || s.fabricComposition || s.material ||
  s.fabricDesc || s.fabricName || '';

const getStyleBrand = (s) =>
  s.brand || s.brandName || s.Brand || '';

const getStyleSeason = (s) =>
  s.season || s.Season || s.seasonCode || '';

const saveToAPI = async (category, type, data) => {
  try {
    const lean = stripBase64(data);
    const res = await fetch(`${API_BASE}/save_universal.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, type, data: lean })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const r = await res.json();
    if (r.id) return r.id;
    if (r.success === false) throw new Error(r.error || 'Server error');
    return r.id || Date.now();
  } catch (e) {
    console.error(`Save failed [${category}/${type}]:`, e.message);
    throw e; // re-throw so callers can show proper error
  }
};

// -----------------------------------------------------------------------------
//  SMART CAPTURE: BOUNDING BOX
// -----------------------------------------------------------------------------
const captureSmartImage = async (containerRef, items) => {
  if (!containerRef.current || items.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  items.forEach(item => {
    minX = Math.min(minX, item.x); minY = Math.min(minY, item.y);
    maxX = Math.max(maxX, item.x + item.w); maxY = Math.max(maxY, item.y + item.h);
  });
  const padding = 40; 
  minX = Math.max(0, minX - padding); minY = Math.max(0, minY - padding);
  const width = (maxX - minX) + (padding * 2); const height = (maxY - minY) + (padding * 2);
  try {
    const canvas = await html2canvas(containerRef.current, { backgroundColor: null, useCORS: true, x: minX, y: minY, width, height, scale: 2 });
    return canvas.toDataURL('image/png');
  } catch (e) { return null; }
};

// -----------------------------------------------------------------------------
//  THREE.JS FABRIC LAYER (Vivid Textures)
// -----------------------------------------------------------------------------
const ThreeFabricLayer = ({ items, globalTextureUrl, parentRef }) => {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(new THREE.Scene());
  const cameraRef = useRef(null);
  const textureCache = useRef({});

  const getSize = () => ({ width: parentRef.current?.clientWidth || 800, height: parentRef.current?.clientHeight || 1200 });
  const loadSourceTexture = (url) => {
    if (!url) return null;
    if (textureCache.current[url]) return textureCache.current[url];
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const tex = loader.load(url, t => {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.colorSpace = THREE.SRGBColorSpace; 
      t.needsUpdate = true;
    });
    textureCache.current[url] = tex;
    return tex;
  };

  useEffect(() => {
    if (!mountRef.current) return;
    const { width, height } = getSize();
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    renderer.toneMapping = THREE.NoToneMapping; 
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    const camera = new THREE.OrthographicCamera(0, width, 0, -height, 0.1, 1000);
    camera.position.z = 10;
    cameraRef.current = camera;
    const animate = () => { requestAnimationFrame(animate); renderer.render(sceneRef.current, camera); };
    animate();
    return () => { renderer.dispose(); if (mountRef.current) mountRef.current.innerHTML = ''; };
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    scene.children.forEach(child => { if (child.material) child.material.dispose(); scene.remove(child); });
    items.forEach(item => {
      const patternUrl = item.localTexture || globalTextureUrl;
      const shapeUrl = item.image;
      if (!patternUrl || !shapeUrl) return;
      const geometry = new THREE.PlaneGeometry(1, 1);
      const material = new THREE.MeshBasicMaterial({ transparent: true, opacity: item.textureOpacity !== undefined ? item.textureOpacity / 100 : 1.0 });
      const mesh = new THREE.Mesh(geometry, material);
      const sourceTex = loadSourceTexture(patternUrl);
      if (sourceTex) {
        const cloned = sourceTex.clone();
        cloned.wrapS = cloned.wrapT = THREE.RepeatWrapping;
        cloned.colorSpace = THREE.SRGBColorSpace;
        const scaleFactor = (item.textureScale || 100) / 100;
        cloned.repeat.set(1 / scaleFactor, 1 / scaleFactor);
        cloned.rotation = THREE.MathUtils.degToRad(item.textureRotation || 0);
        cloned.offset.set(item.textureOffsetX || 0, item.textureOffsetY || 0);
        material.map = cloned;
      }
      const shapeTex = loadSourceTexture(shapeUrl);
      if (shapeTex) material.alphaMap = shapeTex;
      mesh.position.set(item.x + item.w / 2, -(item.y + item.h / 2), 0);
      mesh.scale.set(item.w * (item.flipH ? -1 : 1), item.h * (item.flipV ? -1 : 1), 1);
      mesh.rotation.z = -THREE.MathUtils.degToRad(item.rotate || 0);
      scene.add(mesh);
    });
  }, [items, globalTextureUrl]);

  return <div ref={mountRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }} />;
};

// -----------------------------------------------------------------------------
//  SMV & OB ENGINE
// -----------------------------------------------------------------------------
const calculateBasicTimeSecs = (op, machines, manualCodes) => {
  const freq = parseFloat(op.frequency) || 1;
  if (op.type === 'manual') {
    const ref = (manualCodes || []).find(m => m.id == op.refId);
    return parseFloat(ref?.value || 0) * freq;
  }
  if (op.type === 'machine') {
    const mc = (machines || []).find(m => m.id == op.mcId);
    if (!mc) return 0;
    if (op.directTime) return parseFloat(op.directTime) * freq;
    const RPM = parseFloat(mc.rpm) || 4500;
    const SPC = parseFloat(mc.spc) || 5;
    const McAllow = parseFloat(mc.allowance) || 10;
    const Len = parseFloat(op.length) || 0;
    const StopFactor = parseFloat(op.stoppage) || 0;
    const FabFactor = parseFloat(op.fabric) || 0;
    const sewTimeSecs = ((Len * SPC) / RPM) * 60;
    const withMcAllow = sewTimeSecs * (1 + McAllow / 100);
    return withMcAllow * (1 + StopFactor / 100) * (1 + FabFactor / 100) * freq;
  }
  return 0;
};

const calcProcessSMV = (proc, db) => {
  // Imported processes carry smvOverride — use it directly until user assigns a DB code
  if (proc.smvOverride && proc._needsCode) return parseFloat(proc.smvOverride) || 0;
  const basicSecs = (proc.ops || []).reduce((sum, op) => {
    // directSMV: temporary value stored on ops when no DB code matched at import
    if (op.directSMV) return sum + op.directSMV * 60;
    return sum + calculateBasicTimeSecs(op, db.machines, db.manualCodes);
  }, 0);
  // If basic time is 0 but we have an imported SMV, fall back to it
  if (basicSecs === 0 && proc.importedSMV) return parseFloat(proc.importedSMV) || 0;
  const cont = 3; const pf = 11;
  const withCont = basicSecs * (1 + cont / 100);
  const withPF = withCont * (1 + pf / 100);
  return (withPF / 60) + 0.09;
};

const getDefaultThreadFactor = (machineCode) => {
  const THREAD_FACTORS = { 'OL': 12, 'OVERLOCK': 12, 'OVL': 12, 'FL': 8, 'FLATLOCK': 8, 'SN': 2.5, 'SNS': 2.5, 'SNLS': 2.5, 'DN': 4, 'DNS': 4, 'BH': 3.5, 'BUTTON': 1.5, 'BS': 1.5, 'BAR': 2, 'KM': 3, 'DEFAULT': 3 };
  if (!machineCode) return THREAD_FACTORS.DEFAULT;
  const upper = machineCode.toUpperCase();
  for (const [key, val] of Object.entries(THREAD_FACTORS)) { if (upper.includes(key)) return val; }
  return THREAD_FACTORS.DEFAULT;
};

// -- Thread consumption per process: stitchLength - threadFactor + wastage ----
const calcProcessThread = (proc, db) => {
  let totalThreadCm = 0;
  (proc.ops || []).forEach(op => {
    if (op.type !== 'machine') return;
    const mc = (db.machines || []).find(m => m.id == op.mcId);
    if (!mc) return;
    const stitchLen = parseFloat(op.length) || 0;
    const freq = parseFloat(op.frequency) || 1;
    const factor = parseFloat(mc.threadFactor) || getDefaultThreadFactor(mc.code);
    const wastage = parseFloat(mc.wastageFactor) || 15;
    const raw = stitchLen * factor * freq;
    totalThreadCm += raw * (1 + wastage / 100);
  });
  // Also add perimeter-based thread for seam allowance if ops don't have explicit length
  const hasLengthOps = (proc.ops || []).some(op => op.type === 'machine' && parseFloat(op.length) > 0);
  if (!hasLengthOps && proc.partPerimeter) {
    const machine = (db.machines || []).find(m => proc.ops?.some(op => op.mcId == m.id));
    const factor = machine ? (parseFloat(machine.threadFactor) || getDefaultThreadFactor(machine.code)) : 3;
    const wastage = machine ? (parseFloat(machine.wastageFactor) || 15) : 15;
    totalThreadCm = (proc.partPerimeter || 0) * factor * (1 + wastage / 100);
  }
  return totalThreadCm;
};

const buildOBData = (obSequence, db, style, targetEfficiency, dailyMinutes = 480) => {
  const totalSMV = obSequence.reduce((s, p) => s + calcProcessSMV(p, db), 0);
  const totalWorkstations = obSequence.length;
  const pitchTime = totalWorkstations > 0 ? totalSMV / totalWorkstations : 0;
  const effectivePitch = pitchTime > 0 ? pitchTime : 1;

  let totalThreadCm = 0;
  const machineSet = new Set();
  const rows = obSequence.map((proc, i) => {
    const smv = calcProcessSMV(proc, db);
    const workers = parseFloat((smv / effectivePitch).toFixed(2));
    // Target is efficiency-adjusted; SMV itself is always 100% standard time —
    // never scale smv by targetEfficiency here.
    const hourlyTarget = parseFloat(((60 / smv) * (targetEfficiency / 100)).toFixed(1));
    const machine = (db.machines || []).find(m => proc.ops?.some(op => op.mcId == m.id));
    const mcCode = machine?.code || (proc.ops?.[0]?.type === 'manual' ? 'MANUAL' : 'SEW');
    machineSet.add(mcCode);
    const threadCm = proc.threadOverride != null ? parseFloat(proc.threadOverride) : calcProcessThread(proc, db);
    totalThreadCm += threadCm;

    return {
      seq: i + 1, partName: proc.partName, processName: proc.name, machine: mcCode, attachment: proc.attachment || '', video: proc.video || '',
      workers, hourlyTarget, smv, threadCm: threadCm.toFixed(1), threadCount: proc.threadCount || '', needleNo: proc.needleNo || '',
      threadTkt: proc.threadTkt || '', partImage: proc.partImage || ''
    };
  });

  const totalWorkers = rows.reduce((s, r) => s + r.workers, 0);
  const balancingEfficiency = totalWorkstations > 0 ? ((totalWorkers / totalWorkstations) * 100).toFixed(1) : '0';
  // Daily target: efficiency-adjusted, using unadjusted (100%) totalSMV as the
  // denominator — SMV itself is never scaled by efficiency, only the target is.
  const dailyTarget = totalSMV > 0 ? Math.floor((dailyMinutes / totalSMV) * (targetEfficiency / 100)) : 0;
  const uniqueMachines = machineSet.size;
  const workforceNeeded = Math.ceil(totalWorkers);

  return { rows, totalSMV, totalWorkstations, pitchTime, dailyTarget, balancingEfficiency, totalThreadCm, totalWorkers, workforceNeeded, uniqueMachines, dailyMinutes };
};

const removeWhiteBackground = (src, threshold = 240) => {
  return new Promise((resolve) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas'); canvas.width = img.width; canvas.height = img.height;
      const ctx = canvas.getContext('2d'); ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < data.data.length; i += 4) {
        if (data.data[i] >= threshold && data.data[i + 1] >= threshold && data.data[i + 2] >= threshold) data.data[i + 3] = 0;
      }
      ctx.putImageData(data, 0, 0); resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(src); img.src = src;
  });
};

// -----------------------------------------------------------------------------
//  FULL OPERATION BULLETIN MODAL - with full editing, thread tkt, images, exports
// -----------------------------------------------------------------------------
const downloadCSV = (rows, filename) => {
  const headers = ['SL','Part','Process','Machine','TktNo','Workers','HrlyTarget','SMV','Thread(cm)','ThreadTkt','NeedleNo'];
  const csvRows = [headers.join(','), ...rows.map(r => [r.seq,r.partName,r.processName,r.machine,r.threadTkt||'',r.workers,r.hourlyTarget,r.smv.toFixed(4),r.threadCm,r.threadTkt||'',r.needleNo||''].join(','))];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename + '.csv'; a.click();
};

const downloadExcel = (rows, filename) => {
  try {
    const XLSX = window.XLSX || require('xlsx');
    const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
      'SL': r.seq, 'Part Name': r.partName, 'Process': r.processName, 'Machine': r.machine,
      'Workers': r.workers, 'Hrly Target': r.hourlyTarget, 'SMV (min)': r.smv.toFixed(4),
      'Thread (cm)': r.threadCm, 'Thread Tkt': r.threadTkt || '', 'Needle No': r.needleNo || ''
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'OB');
    XLSX.writeFile(wb, filename + '.xlsx');
  } catch(e) { alert('Excel export requires XLSX library. Downloading CSV instead.'); downloadCSV(rows, filename); }
};

const downloadPDF = (rows, style, ob, showPartImages) => {
  const w = window.open('', '_blank');
  const tableRows = rows.map(r => `
    <tr>
      <td>${r.seq}</td>
      <td>${showPartImages && r.partImage ? `<img src="${r.partImage}" style="height:40px;object-fit:contain;" />` : ''}</td>
      <td>${r.partName}</td><td>${r.processName}</td>
      <td><span class="mc">${r.machine}</span></td>
      <td>${r.threadTkt || '—'}</td>
      <td class="num">${r.workers}</td><td class="num">${r.hourlyTarget}</td>
      <td class="num">${r.smv.toFixed(4)}</td><td class="num">${r.threadCm}</td>
      <td>${r.needleNo || '—'}</td>
    </tr>`).join('');
  w.document.write(`<html><head><title>OB - ${style?.styleRef}</title>
  <style>body{font-family:Arial,sans-serif;font-size:11px;margin:15px}
  h2{color:#323338;margin-bottom:4px}
  .meta{display:flex;gap:20px;margin:8px 0;background:#f5f6f8;padding:8px;border-radius:4px;font-size:10px}
  .meta div{text-align:center} .meta .v{font-weight:bold;font-size:13px}
  table{width:100%;border-collapse:collapse;margin-top:10px}
  th{background:#0073ea;color:white;padding:7px 6px;text-align:left;font-size:10px}
  td{padding:5px 6px;border-bottom:1px solid #eee} tr:nth-child(even){background:#f5f6f8}
  .mc{background:#e8f0ff;color:#0073ea;padding:2px 5px;border-radius:3px;font-weight:bold}
  .num{text-align:right} @media print{button{display:none}}</style>
  </head><body>
  <h2>OPERATION BULLETIN — ${style?.styleRef || ''}</h2>
  <div class="meta">
    <div><div>Brand</div><div class="v">${style?.brand || '—'}</div></div>
    <div><div>Season</div><div class="v">${style?.season || '—'}</div></div>
    <div><div>Total SMV</div><div class="v">${ob.totalSMV.toFixed(3)} min</div></div>
    <div><div>Daily Target</div><div class="v">${ob.dailyTarget} pcs</div></div>
    <div><div>Balancing Eff</div><div class="v">${ob.balancingEfficiency}%</div></div>
    <div><div>Workforce</div><div class="v">${ob.workforceNeeded} workers</div></div>
    <div><div>Machines</div><div class="v">${ob.uniqueMachines} types</div></div>
    <div><div>Working Hours</div><div class="v">${(ob.dailyMinutes/60).toFixed(1)}h</div></div>
    <div><div>Total Thread</div><div class="v">${ob.totalThreadCm.toFixed(1)} cm</div></div>
  </div>
  <table><thead><tr>
    <th>SL</th>${showPartImages ? '<th>Image</th>' : ''}<th>Part</th><th>Process</th><th>Machine</th>
    <th>Thread Tkt</th><th class="num">Workers</th><th class="num">Hrly</th>
    <th class="num">SMV</th><th class="num">Thread(cm)</th><th>Needle</th>
  </tr></thead><tbody>${tableRows}</tbody></table>
  <script>window.print();</script></body></html>`);
  w.document.close();
};

// ─────────────────────────────────────────────────────────────────────────────
// OB IMPORT MODAL — Upload Excel / CSV operation bulletin and map to OB
// Matches: SL No, CPU/LINE (part), Operation Name, M/C or Manual, Machine Name,
//          Machine Code, SMV, Attachment, Thread Tkt, Needle No, Remarks
// User only needs to assign manual/machine codes from the DB after import
// ─────────────────────────────────────────────────────────────────────────────
const OBImportModal = ({ db, onImport, onClose }) => {
  const [step, setStep]           = useState('upload');   // upload | map | review
  const [rawRows, setRawRows]     = useState([]);         // parsed rows from file
  const [headers, setHeaders]     = useState([]);         // display headers (filtered)
  const [rawHeaders, setRawHeaders] = useState([]);       // raw headers WITH position (for index lookup)
  const [colMap, setColMap]       = useState({});         // { ourField: fileHeader }
  const [preview, setPreview]     = useState([]);         // mapped preview rows
  const [error, setError]         = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // Our fields and their auto-detect aliases
  // Rules: exact match = score 10, alias-contains-header = score 7, header-contains-alias = score 4
  // Short-alias guard: aliases <= 3 chars only match if the ENTIRE cell equals the alias (no substring)
  const FIELDS = [
    { key: 'seqNo',      label: 'SL / Seq No',
      aliases: ['serl no','sl no','seq no','sl#','serial no','#','s.no','s/n'], required: false },
    { key: 'partName',   label: 'Part / CPU / Line',
      aliases: ['cpu/line','part name','cpu / line','part','section','line group','cpu line','group'], required: false },
    { key: 'procName',   label: 'Operation Name',
      aliases: ['final operation name','operation name','process name','operation','process','op name','description'], required: true },
    { key: 'mcManual',   label: 'M/C or Manual',
      aliases: ['m/c/ manual','m/c or manual','mc/manual','machine/manual','m/c manual','type'], required: false },
    { key: 'machineName',label: 'Machine Name',
      aliases: ['machine name','m/c name','equipment name','machine'], required: false },
    { key: 'machineCode',label: 'Machine Code',
      aliases: ['machine code','mc code','m/c code','code'], required: false },
    { key: 'smv',        label: 'SMV (minutes)',
      aliases: ['final smv','gsd smv','smv','sam','basic time','std time','standard smv'], required: false },
    { key: 'threadTkt',  label: 'Thread Ticket',
      aliases: ['thread tkt','thread ticket','thread no','thread'], required: false },
    { key: 'needleNo',   label: 'Needle No',
      aliases: ['needle no','needle no.','needle size','needle type','needle'], required: false },
    { key: 'attachment', label: 'Attachment / Guide',
      aliases: ['attachment','guide attachment','att','folder','accessory'], required: false },
    { key: 'remarks',    label: 'Remarks / Notes',
      aliases: ['remarks','remark','notes','note','comment'], required: false },
  ];

  // Auto-detect column mapping — exact match preferred, then contains
  // Short aliases (<=4 chars) must be EXACT match to avoid false positives on title rows
  const autoDetect = (hdrs) => {
    const map = {};
    const lower = hdrs.map(h => (h||'').toString().toLowerCase().trim()); // hdrs may include empty strings from rawHeaders
    FIELDS.forEach(f => {
      let best = null, bestScore = 0;
      f.aliases.forEach(alias => {
        lower.forEach((h, i) => {
          if (!h || h.length > 60) return; // skip empty or suspiciously long cells
          let score = 0;
          if (h === alias) {
            score = 10; // exact match
          } else if (alias.length > 4 && h.includes(alias)) {
            score = 7;  // header contains full alias (e.g. 'final operation name' in 'final operation name (gsd)')
          } else if (alias.length > 4 && alias.includes(h) && h.length > 3) {
            score = 5;  // alias contains header (e.g. alias='machine name', header='machine')
          }
          // Never use substring match for short aliases — causes false positives
          if (score > bestScore) { best = hdrs[i]; bestScore = score; }
        });
      });
      if (best) map[f.key] = best;
    });
    return map;
  };

  // Parse uploaded file — supports Excel (.xlsx/.xls) and CSV
  const handleFile = async (e) => {
    setError('');
    const file = e.target.files[0];
    if (!file) return;

    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let rows = [];

      if (ext === 'csv') {
        const text = await file.text();
        const lines = text.split('\n').map(l => l.split(',').map(c => c.trim().replace(/^"|"$/g, '')));
        if (lines.length < 2) { setError('CSV file appears empty'); return; }
        rows = lines;
      } else if (ext === 'xlsx' || ext === 'xls') {
        const buf = await file.arrayBuffer();
        const wb  = XLSX.read(buf, { type: 'array' });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        rows = data.map(r => r.map(c => (c === null || c === undefined) ? '' : String(c)));
      } else {
        setError('Please upload .xlsx, .xls or .csv file');
        return;
      }

      // Find header row — scan first 8 rows, pick the one with the most EXACT/NEAR-EXACT matches
      // Use quality scoring so a title like "SMV Comparison..." doesn't beat the real header row
      const scoreRow = (row) => {
        let score = 0;
        const cells = row.map(c => (c || '').toString().toLowerCase().trim());
        FIELDS.forEach(f => {
          f.aliases.forEach(alias => {
            cells.forEach(c => {
              if (!c || c.length > 60) return;
              if (c === alias)                            { score += 10; } // exact
              else if (alias.length > 4 && c.includes(alias)) { score += 7; }  // contains
              else if (alias.length > 4 && alias.includes(c) && c.length > 3) { score += 4; }
            });
          });
        });
        // Bonus: penalise rows where the first non-empty cell looks like a title (very long)
        const firstNonEmpty = cells.find(c => c.length > 0) || '';
        if (firstNonEmpty.length > 30) score -= 20;
        return score;
      };

      let headerRowIdx = 0;
      let bestScore = -1;
      for (let i = 0; i < Math.min(8, rows.length); i++) {
        const s = scoreRow(rows[i]);
        if (s > bestScore) { bestScore = s; headerRowIdx = i; }
      }

      // Build headers keeping original positions (don't filter — we need indices to match data rows)
      const rawHdrs = rows[headerRowIdx].map(h => h.toString().trim());
      const hdrs    = rawHdrs.filter(Boolean); // display only, indices resolved via rawHdrs below
      const dataRows = rows.slice(headerRowIdx + 1).filter(r => r.some(c => c && c.toString().trim()));

      setRawHeaders(rawHdrs);   // keep raw positions for index lookups
      setHeaders(hdrs);          // filtered display list for dropdowns
      setRawRows(dataRows);
      setColMap(autoDetect(rawHdrs)); // detect against raw headers so indices align
      setStep('map');
    } catch (err) {
      setError('Failed to parse file: ' + err.message);
    }
  };

  // Build preview from current colMap
  const buildPreview = (map) => {
    const getVal = (row, field) => {
      const hdr = map[field];
      if (!hdr) return '';
      // Use rawHeaders for index lookup — rawHeaders positions align with raw data rows
      const rawIdx = rawHeaders.length > 0 ? rawHeaders.indexOf(hdr) : headers.indexOf(hdr);
      const idx = rawIdx >= 0 ? rawIdx : headers.indexOf(hdr);
      return idx >= 0 && idx < row.length ? (row[idx] || '').toString().trim() : '';
    };

    // For SMV: if column not mapped, try to find first numeric column with SMV-like name
    return rawRows
      .filter(row => row.some(c => c && c.toString().trim()))
      .map((row, i) => {
        const procName = getVal(row, 'procName');
        if (!procName) return null; // skip blank operation rows

        const mcManualRaw = getVal(row, 'mcManual').toUpperCase();
        const isManual    = mcManualRaw === 'H' || mcManualRaw === 'HAND' || mcManualRaw === 'MANUAL';
        const isIron      = mcManualRaw === 'I' || mcManualRaw === 'IRON' || mcManualRaw === 'PRESSING';
        const opType      = isManual ? 'manual' : isIron ? 'iron' : 'machine';

        // Match machine from db.machines by name or code
        const machines = db.machines || [];
        const machineName = getVal(row, 'machineName');
        const machineCode = getVal(row, 'machineCode');
        const matchedMachine = machines.find(m =>
          (m.code && m.code.toUpperCase() === machineCode.toUpperCase()) ||
          (m.name && m.name.toUpperCase() === machineName.toUpperCase())
        );

        // Match manual code from db.manualCodes
        const manualCodes = db.manualCodes || [];
        const matchedManual = isManual ? manualCodes.find(m =>
          (m.code && m.code.toUpperCase() === machineCode.toUpperCase()) ||
          (m.name && m.name.toUpperCase() === machineName.toUpperCase())
        ) : null;

        // Parse SMV — handle NaN string, blank, 0
        const smvStr = getVal(row, 'smv');
        const smvRaw = (smvStr && smvStr.toLowerCase() !== 'nan' && smvStr !== '')
          ? (parseFloat(smvStr) || 0) : 0;

        return {
          _rowIdx: i,
          seqNo:       getVal(row, 'seqNo') || String(i + 1),
          partName:    getVal(row, 'partName') || 'General',
          procName,
          mcManual:    mcManualRaw,
          machineName,
          machineCode,
          smv:         smvRaw,
          threadTkt:   getVal(row, 'threadTkt'),
          needleNo:    getVal(row, 'needleNo'),
          attachment:  getVal(row, 'attachment'),
          remarks:     getVal(row, 'remarks'),
          opType,
          matchedMachine,
          matchedManual,
          smvStatus:   smvRaw > 0 ? 'ok' : 'manual',    // 'ok' = imported, 'manual' = needs entry
          codeStatus:  (matchedMachine || matchedManual) ? 'matched' : opType === 'manual' ? 'needsManual' : 'needsMachine',
        };
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (step === 'map' && rawRows.length > 0) setPreview(buildPreview(colMap));
  }, [colMap, step]);

  // Convert preview rows to obSequence entries
  const doImport = () => {
    setImporting(true);
    const newProcs = preview.map((row, i) => {
      const ops = [];
      if (row.opType === 'manual' && row.matchedManual) {
        ops.push({ type: 'manual', refId: row.matchedManual.id, basicTimeSecs: row.smv * 60 || 0 });
      } else if (row.matchedMachine) {
        ops.push({ type: 'machine', mcId: row.matchedMachine.id, length: 0, frequency: 1, basicTimeSecs: row.smv * 60 || 0 });
      } else {
        // No match — store SMV directly so calc still works
        ops.push({ type: row.opType === 'manual' ? 'manual' : 'machine', directSMV: row.smv, basicTimeSecs: row.smv * 60 || 0 });
      }
      return {
        seqId:       `imp_${Date.now()}_${i}`,
        name:        row.procName,
        partName:    row.partName,
        ops,
        smvOverride: row.smv,        // store imported SMV directly — user can refine later
        importedSMV: row.smv,        // keep original for comparison
        machineName: row.machineName,
        machineCode: row.machineCode,
        mcManual:    row.mcManual,
        opType:      row.opType,
        threadTkt:   row.threadTkt,
        needleNo:    row.needleNo,
        attachment:  row.attachment,
        remarks:     row.remarks,
        _needsCode:  !row.matchedMachine && !row.matchedManual,  // flag for user to assign
      };
    });
    onImport(newProcs);
    setImporting(false);
    onClose();
  };

  const matched   = preview.filter(r => r.codeStatus === 'matched').length;
  const needsCode = preview.filter(r => r._needsCode !== false && r.codeStatus !== 'matched').length;

  const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
  const box     = { background: 'white', borderRadius: 12, width: '100%', maxWidth: 960, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' };
  const hdr     = { padding: '16px 20px', background: '#0F172A', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
  const body    = { flex: 1, overflowY: 'auto', padding: 20 };
  const ftr     = { padding: '12px 20px', borderTop: '1px solid #e6e9ef', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 };
  const btn     = (bg, col='white') => ({ padding: '9px 20px', background: bg, color: col, border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 });
  const sel     = { padding: '5px 8px', fontSize: 12, borderRadius: 5, border: '1px solid #c5c7d4', background: 'white', cursor: 'pointer', minWidth: 130 };
  const pill    = (col) => ({ background: col + '22', color: col, padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' });

  return (
    <div style={modalBg}>
      <div style={box}>
        <div style={hdr}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>📥 Import Operation Bulletin</div>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
              Upload Excel or CSV OB → Auto-map columns → Review & Import
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #323338', color: 'white', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕ Close</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', background: '#F8FAFC', borderBottom: '1px solid #e6e9ef', padding: '0 20px', flexShrink: 0 }}>
          {[['upload','1. Upload File'],['map','2. Map Columns'],['review','3. Review & Import']].map(([s, label]) => (
            <div key={s} style={{ padding: '10px 16px', fontSize: 12, fontWeight: step === s ? 700 : 400, color: step === s ? '#2563EB' : '#94A3B8', borderBottom: step === s ? '2px solid #2563EB' : '2px solid transparent', cursor: 'pointer' }}
              onClick={() => { if (s === 'review' && preview.length > 0) setStep('review'); else if (s === 'map' && rawRows.length > 0) setStep('map'); else if (s === 'upload') setStep('upload'); }}>
              {label}
            </div>
          ))}
        </div>

        <div style={body}>
          {error && <div style={{ padding: '10px 14px', background: '#FEF2F2', color: '#DC2626', borderRadius: 6, marginBottom: 14, fontSize: 13, fontWeight: 600 }}>⚠ {error}</div>}

          {/* ── STEP 1: UPLOAD ── */}
          {step === 'upload' && (
            <div>
              <div style={{ border: '2px dashed #CBD5E1', borderRadius: 10, padding: 40, textAlign: 'center', background: '#F8FAFC', cursor: 'pointer' }}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { const inp = fileRef.current; const dt = new DataTransfer(); dt.items.add(f); inp.files = dt.files; handleFile({ target: inp }); } }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#323338', marginBottom: 6 }}>Drop your Operation Bulletin here</div>
                <div style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>Supports <strong>.xlsx</strong>, <strong>.xls</strong>, and <strong>.csv</strong> formats</div>
                <button style={{ ...btn('#2563EB'), fontSize: 13 }}>Browse File</button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
              </div>

              {/* Template download section */}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#323338', marginBottom: 10 }}>📋 Expected OB Format</div>
                <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#1E293B', color: 'white' }}>
                        {['Serl No','CPU/LINE','FINAL OPERATION NAME','M/C/ MANUAL','MACHINE NAME','MACHINE CODE','SMV','THREAD TKT','NEEDLE NO','ATTACHMENT','REMARKS'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['1','CPU','Attach Pkt Facing To Pkt W/Fold','M','SNLS','1N2TLS','0.9297','T-120','#14','—',''],
                        ['2','CPU','Make Belt Loops (5 Loops)','M','SNLS','1N2TLS','0.3405','T-120','#14','Folder',''],
                        ['3','CPU','Attach Zipper to Single Fly','M','DNLS','2N4TLS','0.2064','T-120','#16','Zipper foot',''],
                        ['4','ASM-FRONT','O/L Front Rise Single Fly','M','OL 3T','1N3TOL','0.3821','T-120','—','—',''],
                        ['5','FINISHING','Final Iron & Pack','I','IRON','IRON','0.2500','—','—','—',''],
                      ].map((r, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#F8FAFC' }}>
                          {r.map((c, j) => <td key={j} style={{ padding: '6px 10px', color: '#676879', borderBottom: '1px solid #F1F5F9' }}>{c}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 10, fontSize: 11, color: '#94A3B8' }}>
                  <strong>M/C or MANUAL column:</strong> M = Machine &nbsp;·&nbsp; H = Manual/Hand &nbsp;·&nbsp; I = Iron/Pressing
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <strong>Machine Code</strong> must match codes in Tech Masters for auto-linking
                </div>
                {/* Download template button */}
                <button style={{ ...btn('#059669'), marginTop: 12, fontSize: 12 }} onClick={() => {
                  const templateData = [
                    ['Serl No','CPU/LINE','FINAL OPERATION NAME','M/C/ MANUAL','MACHINE NAME','MACHINE CODE','SMV','THREAD TKT','NEEDLE NO','ATTACHMENT','REMARKS'],
                    ['1','CPU','Operation Name Here','M','SNLS','1N2TLS','0.0000','T-120','#14','',''],
                    ['2','CPU','Another Operation','H','Table','Table','0.0000','—','—','',''],
                    ['3','ASM-FRONT','Front Assembly Op','M','OL 3T','1N3TOL','0.0000','T-120','—','',''],
                    ['4','FINISHING','Final Pressing','I','IRON','IRON','0.0000','—','—','',''],
                  ];
                  const wb = XLSX.utils.book_new();
                  const ws = XLSX.utils.aoa_to_sheet(templateData);
                  ws['!cols'] = [8,12,35,12,14,14,10,10,10,14,20].map(w => ({ wch: w }));
                  XLSX.utils.book_append_sheet(wb, ws, 'OB Template');
                  XLSX.writeFile(wb, 'OB_Template.xlsx');
                }}>⬇ Download OB Template (.xlsx)</button>
              </div>
            </div>
          )}

          {/* ── STEP 2: MAP COLUMNS ── */}
          {step === 'map' && (
            <div>
              <div style={{ marginBottom: 14, padding: '10px 14px', background: '#EFF6FF', borderRadius: 6, fontSize: 13 }}>
                <strong>Auto-detected {Object.keys(colMap).length} of {FIELDS.length} columns</strong> from your file. Adjust any that look wrong, then click <em>Build Preview</em>.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 12 }}>
                {FIELDS.map(f => (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: colMap[f.key] ? '#F0FDF4' : '#FFF', border: `1px solid ${colMap[f.key] ? '#86EFAC' : '#E2E8F0'}`, borderRadius: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#323338' }}>{f.label}{f.required && <span style={{ color: '#DC2626' }}> *</span>}</div>
                    </div>
                    <select value={colMap[f.key] || ''} onChange={e => setColMap(m => ({ ...m, [f.key]: e.target.value || undefined }))} style={sel}>
                      <option value="">— not mapped —</option>
                      {(rawHeaders.length > 0 ? rawHeaders : headers).filter(Boolean).map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {colMap[f.key] && <span style={{ color: '#059669', fontSize: 14 }}>✓</span>}
                  </div>
                ))}
              </div>

              {/* SMV multi-column note */}
              <div style={{ marginTop: 16, padding: '10px 14px', background: '#FFFBEB', borderRadius: 6, fontSize: 12, color: '#92400E' }}>
                💡 If your OB has <strong>multiple SMV columns</strong> (e.g. different styles), map the one you want to import. Others will be ignored.
              </div>
            </div>
          )}

          {/* ── STEP 3: REVIEW ── */}
          {step === 'review' && (
            <div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ padding: '8px 16px', background: '#ECFDF5', borderRadius: 6, fontSize: 13 }}>
                  <strong style={{ color: '#059669' }}>{preview.length}</strong> <span style={{ color: '#676879' }}>operations</span>
                </div>
                <div style={{ padding: '8px 16px', background: '#EFF6FF', borderRadius: 6, fontSize: 13 }}>
                  <strong style={{ color: '#2563EB' }}>{matched}</strong> <span style={{ color: '#676879' }}>auto-matched to DB machines/codes</span>
                </div>
                {needsCode > 0 && (
                  <div style={{ padding: '8px 16px', background: '#FFFBEB', borderRadius: 6, fontSize: 13 }}>
                    <strong style={{ color: '#D97706' }}>{needsCode}</strong> <span style={{ color: '#676879' }}>need manual/machine code assignment after import</span>
                  </div>
                )}
                <div style={{ padding: '8px 16px', background: '#F5F3FF', borderRadius: 6, fontSize: 13 }}>
                  <strong style={{ color: '#7C3AED' }}>{preview.reduce((s,r) => s + (r.smv||0), 0).toFixed(3)}</strong> <span style={{ color: '#676879' }}>total SMV</span>
                </div>
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: '#1E293B', color: 'white', position: 'sticky', top: 0 }}>
                      {['#','Part / Section','Operation Name','Type','Machine','Code','SMV','Thread','Needle','Attachment','DB Match','Remarks'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} style={{ background: row._needsCode ? '#FFFBEB' : i%2===0 ? 'white' : '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '6px 10px', color: '#94A3B8' }}>{row.seqNo}</td>
                        <td style={{ padding: '6px 10px' }}><span style={{ background: '#E0F2FE', color: '#0369A1', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{row.partName}</span></td>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: '#1E293B', maxWidth: 200 }}>{row.procName}</td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={pill(row.opType==='manual'?'#7C3AED':row.opType==='iron'?'#0891B2':'#059669')}>
                            {row.opType==='manual'?'HAND':row.opType==='iron'?'IRON':'MACHINE'}
                          </span>
                        </td>
                        <td style={{ padding: '6px 10px', color: '#676879' }}>{row.machineName || '—'}</td>
                        <td style={{ padding: '6px 10px' }}><span style={{ fontFamily: 'monospace', fontSize: 10, color: '#2563EB' }}>{row.machineCode || '—'}</span></td>
                        <td style={{ padding: '6px 10px', fontWeight: 700, color: row.smv > 0 ? '#059669' : '#64748B' }}>{row.smv > 0 ? row.smv.toFixed(4) : <span style={{color:'#94A3B8',fontStyle:'italic'}}>no SMV</span>}</td>
                        <td style={{ padding: '6px 10px', color: '#64748B' }}>{row.threadTkt || '—'}</td>
                        <td style={{ padding: '6px 10px', color: '#64748B' }}>{row.needleNo || '—'}</td>
                        <td style={{ padding: '6px 10px', color: '#64748B' }}>{row.attachment || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>
                          {row.matchedMachine
                            ? <span style={pill('#059669')}>✓ Machine</span>
                            : row.matchedManual
                            ? <span style={pill('#7C3AED')}>✓ Manual</span>
                            : <span style={pill('#D97706')}>⚠ Assign</span>}
                        </td>
                        <td style={{ padding: '6px 10px', color: '#94A3B8', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.remarks || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {needsCode > 0 && (
                <div style={{ marginTop: 14, padding: '12px 16px', background: '#FFFBEB', borderRadius: 8, fontSize: 12, color: '#92400E', border: '1px solid #FDE68A' }}>
                  <strong>⚠ {needsCode} operations need code assignment after import:</strong> These will be imported with their SMV values. After importing, open the OB table and assign Machine or Manual codes from Tech Masters. SMV will recalculate automatically once codes are linked.
                </div>
              )}
            </div>
          )}
        </div>

        <div style={ftr}>
          <button onClick={onClose} style={btn('#F1F5F9','#676879')}>Cancel</button>
          {step === 'upload' && null}
          {step === 'map' && (
            <>
              <button onClick={() => setStep('upload')} style={btn('#F1F5F9','#676879')}>← Back</button>
              <button onClick={() => { setPreview(buildPreview(colMap)); setStep('review'); }}
                disabled={!FIELDS.find(f => f.required && !colMap[f.key]) === false}
                style={btn('#2563EB')}>Build Preview →</button>
            </>
          )}
          {step === 'review' && (
            <>
              <button onClick={() => setStep('map')} style={btn('#F1F5F9','#676879')}>← Back</button>
              <button onClick={doImport} disabled={importing || preview.length === 0} style={btn('#059669')}>
                {importing ? '…' : `✓ Import ${preview.length} Operations`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const FullOBModal = ({ obSequence, db, style, targetEff, setTargetEff, shiftHours, setShiftHours, onClose, onSeqChange, onSaveOB }) => {
  const [localSeq, setLocalSeq] = useState(() => obSequence.map(p => ({ ...p })));
  const [editRow, setEditRow] = useState(null);   // index of row being fully edited
  const [editData, setEditData] = useState({});   // temp edit state
  const [showPartImages, setShowPartImages] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localEff, setLocalEff] = useState(targetEff);
  const [localShiftHours, setLocalShiftHours] = useState(shiftHours || 8);
  const ob = useMemo(() => buildOBData(localSeq, db, style, localEff, localShiftHours * 60), [localSeq, db, style, localEff, localShiftHours]);

  const moveSeq = (idx, dir) => {
    if (idx + dir < 0 || idx + dir >= localSeq.length) return;
    const next = [...localSeq]; [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
    setLocalSeq(next);
  };

  const startEdit = (idx) => {
    setEditRow(idx);
    const r = ob.rows[idx];
    setEditData({
      partName: r.partName, processName: r.processName, machine: r.machine,
      workers: r.workers, hourlyTarget: r.hourlyTarget, smv: r.smv.toFixed(4),
      threadCm: r.threadCm, threadTkt: r.threadTkt || '', needleNo: r.needleNo || '',
      partImage: localSeq[idx]?.partImage || ''
    });
  };

  const commitEdit = (idx) => {
    setLocalSeq(prev => prev.map((p, i) => i !== idx ? p : {
      ...p,
      partName: editData.partName,
      name: editData.processName,
      threadOverride: editData.threadCm !== ob.rows[idx].threadCm ? parseFloat(editData.threadCm) : undefined,
      threadTkt: editData.threadTkt,
      needleNo: editData.needleNo,
      partImage: editData.partImage,
      // Store manual overrides for workers/target
      workersOverride: parseFloat(editData.workers),
      targetOverride: parseFloat(editData.hourlyTarget),
    }));
    setEditRow(null);
  };

  const handlePartImageUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setEditData(d => ({ ...d, partImage: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setTargetEff(localEff);
    setShiftHours?.(localShiftHours);
    await onSaveOB(localSeq, localEff, localShiftHours);
    onSeqChange(localSeq);
    setIsSaving(false);
    alert('OB saved and linked to this style!');
    onClose();
  };

  const fname = `OB_${style?.styleRef || 'style'}_${new Date().toLocaleDateString().replace(/\//g,'-')}`;

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'white', zIndex: 99999, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '10px 20px', background: '#323338', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>OPERATION BULLETIN — FULL EDIT</h3>
          <div style={{ fontSize: '0.72rem', color: '#9699a6' }}>{style?.styleRef} · {style?.brand} · {style?.season} — Click any row to edit all fields</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.78rem', color: '#c5c7d4', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input type="checkbox" checked={showPartImages} onChange={e => setShowPartImages(e.target.checked)} /> Part Images
          </label>
          <label style={{ fontSize: '0.78rem', color: '#c5c7d4' }}>Eff %</label>
          <input type="number" value={localEff} onChange={e => { const v = +e.target.value; setLocalEff(v); setTargetEff(v); }} min={1} max={150}
            style={{ width: '55px', padding: '4px 6px', borderRadius: '6px', border: 'none', fontSize: '0.88rem', textAlign: 'center' }} />
          <label style={{ fontSize: '0.78rem', color: '#c5c7d4' }}>Shift Hrs</label>
          <input type="number" step="0.5" value={localShiftHours} onChange={e => setLocalShiftHours(+e.target.value)} min={1} max={24}
            style={{ width: '50px', padding: '4px 6px', borderRadius: '6px', border: 'none', fontSize: '0.88rem', textAlign: 'center' }} />
          <button onClick={() => downloadCSV(ob.rows, fname)} style={{ padding: '6px 11px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>⬇ CSV</button>
          <button onClick={() => downloadExcel(ob.rows, fname)} style={{ padding: '6px 11px', background: '#00a65e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>⬇ Excel</button>
          <button onClick={() => downloadPDF(ob.rows, style, ob, showPartImages)} style={{ padding: '6px 11px', background: '#c93652', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>⬇ PDF</button>
          <button onClick={handleSave} disabled={isSaving} style={{ padding: '6px 14px', background: '#0073ea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}>
            {isSaving ? '…' : '💾 SAVE OB'}
          </button>
          <button onClick={onClose} style={{ padding: '6px 12px', background: '#676879', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>✕</button>
        </div>
      </div>

      {/* KPI Bar */}
      <div style={{ background: '#f5f6f8', borderBottom: '1px solid #e6e9ef', padding: '8px 20px', display: 'flex', gap: '20px', flexWrap: 'wrap', flexShrink: 0 }}>
        {[
          ['Total SMV', ob.totalSMV.toFixed(3) + ' min'],
          ['Pitch Time', ob.pitchTime.toFixed(3) + ' min'],
          ['Daily Target', ob.dailyTarget + ' pcs'],
          ['Balancing Eff', ob.balancingEfficiency + '%'],
          ['Workstations', ob.totalWorkstations],
          ['Workforce Needed', ob.workforceNeeded + ' workers'],
          ['Machines', ob.uniqueMachines + ' types'],
          ['Working Hours', localShiftHours + 'h'],
          ['Total Thread', ob.totalThreadCm.toFixed(1) + ' cm'],
        ].map(([k, v]) => (
          <div key={k} style={{ textAlign: 'center', background: 'white', border: '1px solid #e6e9ef', borderRadius: '6px', padding: '6px 14px' }}>
            <div style={{ fontSize: '0.62rem', color: '#676879', fontWeight: 'bold', textTransform: 'uppercase' }}>{k}</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#323338' }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: '#0073ea', color: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
              {['SL', showPartImages && 'Image', 'Part Name', 'Process Name', 'Machine', 'Thread Tkt', 'Workers', 'Hrly Target', 'SMV', 'Thread (cm)', 'Needle No', 'Move', 'Edit'].filter(Boolean).map(h =>
                <th key={h} style={{ padding: '9px 8px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {ob.rows.map((r, i) => (
              <React.Fragment key={i}>
                <tr style={{ borderBottom: '1px solid #e6e9ef', background: editRow === i ? '#fffbeb' : localSeq[i]?._needsCode ? '#FFFBEB' : i % 2 === 0 ? 'white' : '#f5f6f8' }}>
                  <td style={{ padding: '7px 8px', fontWeight: 'bold', color: '#9699a6' }}>{r.seq}</td>
                  {showPartImages && (
                    <td style={{ padding: '4px 8px' }}>
                      {localSeq[i]?.partImage
                        ? <img src={localSeq[i].partImage} style={{ height: 36, width: 36, objectFit: 'contain', borderRadius: 3 }} alt="" />
                        : <div style={{ height: 36, width: 36, background: '#f0f1f4', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#9699a6' }}>img</div>}
                    </td>
                  )}
                  <td style={{ padding: '7px 8px' }}>{r.partName}</td>
                  <td style={{ padding: '7px 8px', fontWeight: '600' }}>
                    {localSeq[i]?._needsCode && <span style={{ fontSize: 9, background: '#FEF3C7', color: '#D97706', padding: '1px 5px', borderRadius: 8, fontWeight: 700, marginRight: 5 }}>⚠ ASSIGN CODE</span>}
                    {localSeq[i]?.importedSMV && !localSeq[i]?._needsCode && <span style={{ fontSize: 9, background: '#ECFDF5', color: '#059669', padding: '1px 5px', borderRadius: 8, fontWeight: 700, marginRight: 5 }}>✓ IMPORTED</span>}
                    {r.processName}
                  </td>
                  <td style={{ padding: '7px 8px' }}><span style={{ background: '#e8f0ff', color: '#0073ea', padding: '2px 6px', borderRadius: '3px', fontSize: '0.72rem', fontWeight: 'bold' }}>{r.machine}</span></td>
                  <td style={{ padding: '7px 8px', color: '#a25ddc', fontWeight: 'bold' }}>{r.threadTkt || <span style={{ color: '#c5c7d4' }}>—</span>}</td>
                  <td style={{ padding: '7px 8px' }}><span style={{ background: r.workers > 1 ? '#fff5e6' : '#e3faf0', color: r.workers > 1 ? '#92400e' : '#166534', padding: '2px 6px', borderRadius: '3px', fontWeight: 'bold' }}>{r.workers}</span></td>
                  <td style={{ padding: '7px 8px', fontWeight: 'bold', color: '#059669' }}>{r.hourlyTarget}</td>
                  <td style={{ padding: '7px 8px', fontWeight: 'bold' }}>{r.smv.toFixed(4)}</td>
                  <td style={{ padding: '7px 8px' }}>{r.threadCm}</td>
                  <td style={{ padding: '7px 8px', color: '#676879' }}>{r.needleNo || '—'}</td>
                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => moveSeq(i, -1)} disabled={i === 0} style={{ padding: '3px 5px', marginRight: '3px', cursor: 'pointer', background: '#f0f1f4', border: '1px solid #ddd', borderRadius: '3px' }}><ArrowUp size={11} /></button>
                    <button onClick={() => moveSeq(i, 1)} disabled={i === ob.rows.length - 1} style={{ padding: '3px 5px', cursor: 'pointer', background: '#f0f1f4', border: '1px solid #ddd', borderRadius: '3px' }}><ArrowDown size={11} /></button>
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <button onClick={() => editRow === i ? setEditRow(null) : startEdit(i)}
                      style={{ padding: '4px 8px', background: editRow === i ? '#fdab3d' : '#e8f0ff', color: editRow === i ? 'white' : '#0073ea', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold' }}>
                      {editRow === i ? 'Cancel' : '✏ Edit'}
                    </button>
                  </td>
                </tr>

                {/* Inline edit row */}
                {editRow === i && (
                  <tr style={{ background: '#fffbeb', borderBottom: '2px solid #fdab3d' }}>
                    <td colSpan={showPartImages ? 14 : 13} style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                        {[
                          ['Part Name', 'partName', 'text'],
                          ['Process Name', 'processName', 'text'],
                          ['Machine Code', 'machine', 'text'],
                          ['Thread Tkt No', 'threadTkt', 'text'],
                          ['Workers', 'workers', 'number'],
                          ['Hrly Target', 'hourlyTarget', 'number'],
                          ['SMV (min)', 'smv', 'number'],
                          ['Thread (cm)', 'threadCm', 'number'],
                          ['Needle No', 'needleNo', 'text'],
                        ].map(([label, field, type]) => (
                          <div key={field}>
                            <label style={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>{label}</label>
                            <input type={type} value={editData[field] ?? ''} onChange={e => setEditData(d => ({ ...d, [field]: e.target.value }))}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #fdab3d', borderRadius: '5px', fontSize: '0.8rem', boxSizing: 'border-box' }} />
                          </div>
                        ))}
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>Thread (Machine)</label>
                          <select value={editData.machine} onChange={e => setEditData(d => ({ ...d, machine: e.target.value }))}
                            style={{ width: '100%', padding: '6px', border: '1px solid #fdab3d', borderRadius: '5px', fontSize: '0.8rem' }}>
                            <option value="">Manual / Custom</option>
                            {(db.machines || []).map(m => <option key={m.id} value={m.code}>{m.code} (Factor:{m.threadFactor||'?'}, Wst:{m.wastageFactor||'?'}%)</option>)}
                          </select>
                        </div>
                        {localSeq[i]?._needsCode && (
                          <>
                            <div style={{ gridColumn: 'span 2', padding: '8px 10px', background: '#FFFBEB', borderRadius: 6, border: '1px solid #FDE68A', fontSize: 11 }}>
                              <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 6 }}>⚠ Assign DB Code — Imported: <span style={{ fontFamily: 'monospace', color: '#2563EB' }}>{localSeq[i]?.machineCode || '?'} / {localSeq[i]?.machineName || '?'}</span></div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: '#64748B', marginBottom: 3 }}>Link Machine</div>
                                  <select style={{ width: '100%', padding: '5px', fontSize: 11, borderRadius: 6, border: '1px solid #FDE68A' }}
                                    defaultValue=""
                                    onChange={e => {
                                      const mcId = e.target.value;
                                      if (!mcId) return;
                                      const mc = (db.machines||[]).find(m => String(m.id) === mcId);
                                      if (!mc) return;
                                      setLocalSeq(prev => prev.map((p, pi) => pi !== i ? p : {
                                        ...p,
                                        _needsCode: false,
                                        ops: [{ type: 'machine', mcId: mc.id, length: 0, frequency: 1, basicTimeSecs: (p.importedSMV||0) * 60 }],
                                      }));
                                    }}>
                                    <option value="">— select machine —</option>
                                    {(db.machines||[]).map(m => <option key={m.id} value={String(m.id)}>{m.code} · {m.name||''}</option>)}
                                  </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: '#64748B', marginBottom: 3 }}>Link Manual Code</div>
                                  <select style={{ width: '100%', padding: '5px', fontSize: 11, borderRadius: 6, border: '1px solid #FDE68A' }}
                                    defaultValue=""
                                    onChange={e => {
                                      const refId = e.target.value;
                                      if (!refId) return;
                                      setLocalSeq(prev => prev.map((p, pi) => pi !== i ? p : {
                                        ...p,
                                        _needsCode: false,
                                        ops: [{ type: 'manual', refId, basicTimeSecs: (p.importedSMV||0) * 60 }],
                                      }));
                                    }}>
                                    <option value="">— select manual code —</option>
                                    {(db.manualCodes||[]).map(m => <option key={m.id} value={String(m.id)}>{m.code} · {m.name||''}</option>)}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>Part Image</label>
                          <label style={{ display: 'block', border: '1px dashed #fdab3d', borderRadius: '5px', padding: '6px', textAlign: 'center', cursor: 'pointer', fontSize: '0.72rem', color: '#92400e' }}>
                            {editData.partImage ? '📷 Change' : '+ Upload Image'}
                            <input type="file" accept="image/*" hidden onChange={handlePartImageUpload} />
                          </label>
                          {editData.partImage && <img src={editData.partImage} style={{ width: '100%', height: 40, objectFit: 'contain', marginTop: 4, borderRadius: 3, border: '1px solid #fde68a' }} alt="" />}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => commitEdit(i)} style={{ padding: '8px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.82rem' }}>✓ Apply Changes</button>
                        <button onClick={() => setEditRow(null)} style={{ padding: '8px 14px', background: '#f0f1f4', color: '#676879', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>,
    document.body
  );
};


// -----------------------------------------------------------------------------
//  IMAGE SEARCH HELPERS
// -----------------------------------------------------------------------------
// =============================================================================
//  ADVANCED IMAGE SIMILARITY ENGINE  — multi-algorithm weighted ensemble
//  Algorithms: perceptual hash (DCT) + colour histogram (HSV) + edge map
//  Each returns 0-100; final score = weighted average
// =============================================================================

// 1. Resize image to canvas of given size, return ImageData
const getPixels = (src, size) => new Promise(resolve => {
  const img = new Image(); img.crossOrigin = 'anonymous';
  img.onload = () => {
    const c = document.createElement('canvas'); c.width = size; c.height = size;
    c.getContext('2d').drawImage(img, 0, 0, size, size);
    resolve(c.getContext('2d').getImageData(0, 0, size, size));
  };
  img.onerror = () => resolve(null); img.src = src;
});

// 2. Perceptual Hash — 8x8 DCT-based, returns 64-bit hash as array of 0/1
const pHash = async (src) => {
  const SIZE = 32; // compute on 32x32, reduce to 8x8 DCT
  const pd = await getPixels(src, SIZE);
  if (!pd) return null;
  // Convert to greyscale
  const grey = [];
  for (let i = 0; i < pd.data.length; i += 4)
    grey.push(0.299 * pd.data[i] + 0.587 * pd.data[i+1] + 0.114 * pd.data[i+2]);
  // 2D DCT (simplified — just top-left 8x8 of 32x32 DCT)
  const dct = [];
  for (let u = 0; u < 8; u++) {
    for (let v = 0; v < 8; v++) {
      let sum = 0;
      for (let x = 0; x < SIZE; x++)
        for (let y = 0; y < SIZE; y++)
          sum += grey[x * SIZE + y] * Math.cos((2*x+1)*u*Math.PI/64) * Math.cos((2*y+1)*v*Math.PI/64);
      dct.push(sum);
    }
  }
  dct[0] = 0; // remove DC component
  const avg = dct.reduce((a,b)=>a+b,0) / dct.length;
  return dct.map(v => v >= avg ? 1 : 0);
};

const hammingDistance = (h1, h2) => {
  if (!h1 || !h2 || h1.length !== h2.length) return 64;
  return h1.reduce((s, v, i) => s + (v !== h2[i] ? 1 : 0), 0);
};

const pHashSimilarity = (h1, h2) => {
  const dist = hammingDistance(h1, h2);
  return Math.max(0, Math.round((1 - dist / 64) * 100));
};

// 3. HSV Colour Histogram — 8 hue + 4 sat + 4 val buckets = 16 bins
const hsvHistogram = async (src) => {
  const pd = await getPixels(src, 64);
  if (!pd) return null;
  const bins = new Array(16).fill(0);
  let count = 0;
  for (let i = 0; i < pd.data.length; i += 4) {
    if (pd.data[i+3] < 100) continue; // skip transparent
    const r = pd.data[i]/255, g = pd.data[i+1]/255, b = pd.data[i+2]/255;
    const max = Math.max(r,g,b), min = Math.min(r,g,b), d = max - min;
    const v = max, s = max === 0 ? 0 : d/max;
    let h = 0;
    if (d > 0) {
      if (max===r) h=(g-b)/d%6; else if (max===g) h=(b-r)/d+2; else h=(r-g)/d+4;
      h = ((h*60)+360)%360;
    }
    bins[Math.min(7, Math.floor(h/45))]++;         // 8 hue bins
    bins[8  + Math.min(3, Math.floor(s*4))]++;     // 4 sat bins
    bins[12 + Math.min(3, Math.floor(v*4))]++;     // 4 val bins
    count++;
  }
  if (count === 0) return bins;
  return bins.map(b => b / count);
};

const histogramSimilarity = (h1, h2) => {
  if (!h1 || !h2) return 0;
  // Bhattacharyya coefficient
  let bc = 0;
  for (let i = 0; i < h1.length; i++) bc += Math.sqrt(h1[i] * h2[i]);
  return Math.round(bc * 100);
};

// 4. Edge Density Map — Sobel on 16x16 greyscale
const edgeMap = async (src) => {
  const SIZE = 16;
  const pd = await getPixels(src, SIZE);
  if (!pd) return null;
  const grey = [];
  for (let i = 0; i < pd.data.length; i += 4)
    grey.push((pd.data[i] + pd.data[i+1] + pd.data[i+2]) / 3);
  const edges = [];
  for (let y = 1; y < SIZE-1; y++) {
    for (let x = 1; x < SIZE-1; x++) {
      const gx = -grey[(y-1)*SIZE+(x-1)] + grey[(y-1)*SIZE+(x+1)] - 2*grey[y*SIZE+(x-1)] + 2*grey[y*SIZE+(x+1)] - grey[(y+1)*SIZE+(x-1)] + grey[(y+1)*SIZE+(x+1)];
      const gy = -grey[(y-1)*SIZE+(x-1)] - 2*grey[(y-1)*SIZE+x] - grey[(y-1)*SIZE+(x+1)] + grey[(y+1)*SIZE+(x-1)] + 2*grey[(y+1)*SIZE+x] + grey[(y+1)*SIZE+(x+1)];
      edges.push(Math.sqrt(gx*gx+gy*gy));
    }
  }
  const max = Math.max(...edges, 1);
  return edges.map(e => e/max);
};

const edgeSimilarity = (e1, e2) => {
  if (!e1 || !e2 || e1.length !== e2.length) return 0;
  let dot=0, n1=0, n2=0;
  for (let i = 0; i < e1.length; i++) { dot += e1[i]*e2[i]; n1 += e1[i]**2; n2 += e2[i]**2; }
  return Math.round((dot / (Math.sqrt(n1)*Math.sqrt(n2)+1e-6)) * 100);
};

// 5. Master similarity — compute all features, return weighted score + breakdown
const computeSimilarityFeatures = async (src) => {
  const [hash, hist, edges] = await Promise.all([pHash(src), hsvHistogram(src), edgeMap(src)]);
  return { hash, hist, edges, src };
};

const computeEnsembleSimilarity = (featA, featB) => {
  const ph  = pHashSimilarity(featA.hash, featB.hash);
  const col = histogramSimilarity(featA.hist, featB.hist);
  const edg = edgeSimilarity(featA.edges, featB.edges);
  // Weights: structure (pHash) 45%, colour 30%, edge 25%
  const score = Math.round(ph * 0.45 + col * 0.30 + edg * 0.25);
  return { score, breakdown: { structural: ph, colour: col, edge: edg } };
};

// Cache features to avoid recomputing on every comparison
const _featureCache = new Map();
const getCachedFeatures = async (src, id) => {
  const key = id || src.slice(0, 40);
  if (_featureCache.has(key)) return _featureCache.get(key);
  const feat = await computeSimilarityFeatures(src);
  _featureCache.set(key, feat);
  return feat;
};

// Legacy compatibility — keep for existing imageSearch in parts panel
const analyzeImageColor = (src) => new Promise((resolve) => {
  const img = new Image(); img.crossOrigin = 'anonymous';
  img.onload = () => {
    const c = document.createElement('canvas'); c.width = 32; c.height = 32;
    const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, 32, 32);
    const d = ctx.getImageData(0, 0, 32, 32).data;
    let r=0,g=0,b=0,n=0;
    for(let i=0;i<d.length;i+=4) if(d[i+3]>100){r+=d[i];g+=d[i+1];b+=d[i+2];n++;}
    resolve(n>0 ? {r:r/n,g:g/n,b:b/n} : null);
  };
  img.onerror=()=>resolve(null); img.src=src;
});
const colorSimilarity=(f1,f2)=>{
  if(!f1||!f2) return 0;
  return Math.max(0,(1-Math.sqrt((f1.r-f2.r)**2+(f1.g-f2.g)**2+(f1.b-f2.b)**2)/441)*100);
};


// =============================================================================
//  STYLE SMART MATCH MODAL
//  3 phases: 1) Find closest style  2) Part gap analysis  3) SMV forecast
// =============================================================================
const StyleSmartMatchModal = ({ uploadedImage, uploadedFeats, db, onSelectStyle, onClose }) => {
  const [phase, setPhase]           = React.useState('matching');  // matching|gaps|smv
  const [styleScores, setStyleScores] = React.useState([]);        // [{style, score, breakdown}]
  const [selectedStyle, setSelectedStyle] = React.useState(null);
  const [partGaps, setPartGaps]     = React.useState([]);          // [{canvasPart, score, replacements}]
  const [smvForecast, setSmvForecast] = React.useState(null);
  const [scanning, setScanning]     = React.useState(true);
  const [scanProgress, setScanProgress] = React.useState(0);

  const allStyles = db.mgmtStyles || [];
  const allParts  = db.masterParts || db.parts || [];

  // Phase 1: score all styles
  React.useEffect(() => {
    if (!uploadedFeats) return;
    const stylesWithImages = allStyles.filter(s => s.img || s.image || s.workstationData?.frontImage);
    if (stylesWithImages.length === 0) { setScanning(false); return; }

    let done = 0;
    const results = [];
    const run = async () => {
      for (const style of stylesWithImages) {
        const src = style.img || style.image || style.workstationData?.frontImage;
        const feat = await getCachedFeatures(src, String(style.id));
        const { score, breakdown } = computeEnsembleSimilarity(uploadedFeats, feat);
        results.push({ style, score, breakdown });
        done++;
        setScanProgress(Math.round(done / stylesWithImages.length * 100));
      }
      results.sort((a, b) => b.score - a.score);
      setStyleScores(results);
      setScanning(false);
    };
    run();
  }, [uploadedFeats]);

  // Phase 2: analyse part gaps for chosen style
  const analyseGaps = async (style) => {
    setSelectedStyle(style);
    setPhase('gaps');
    const canvasParts = style.workstationData?.items || [];
    const results = [];
    for (const cp of canvasParts) {
      if (!cp.image) { results.push({ cp, score: 0, replacements: [], status: 'no-image' }); continue; }
      const cpFeat = await getCachedFeatures(cp.image, `cp_${cp.instId}`);
      // Score against query image (is this part in the new garment?)
      const { score: matchScore } = computeEnsembleSimilarity(uploadedFeats, cpFeat);
      // Find replacement parts from library if score < 100
      let replacements = [];
      if (matchScore < 95) {
        const partScores = [];
        for (const lp of allParts) {
          if (!lp.image) continue;
          const lpFeat = await getCachedFeatures(lp.image, String(lp.id));
          const { score: ps, breakdown } = computeEnsembleSimilarity(cpFeat, lpFeat);
          if (ps > 40) partScores.push({ part: lp, score: ps, breakdown });
        }
        partScores.sort((a,b) => b.score - a.score);
        replacements = partScores.slice(0, 5);
      }
      results.push({ cp, score: matchScore, replacements, status: matchScore >= 95 ? 'matched' : matchScore >= 60 ? 'partial' : 'gap' });
    }
    setPartGaps(results);
  };

  // Phase 3: SMV forecast
  const buildForecast = () => {
    setPhase('smv');
    const FULL    = partGaps.filter(g => g.status === 'matched');
    const PARTIAL = partGaps.filter(g => g.status === 'partial');
    const GAPS    = partGaps.filter(g => g.status === 'gap');

    const smvOf = (items) => items.reduce((s, g) => {
      const procs = g.cp?.processes || [];
      return s + procs.reduce((ps, p) => ps + (parseFloat(p.smvOverride || p.importedSMV) || 0), 0);
    }, 0);

    const smvFull    = smvOf(FULL);
    const smvPartial = smvOf(PARTIAL);   // rough from partial match
    // Best replacement SMV for gaps
    const smvReplacement = GAPS.reduce((s, g) => {
      const best = g.replacements[0];
      if (!best) return s;
      const rSmv = (best.part.processes||[]).reduce((ps,p)=>ps+(parseFloat(p.smvOverride||p.importedSMV)||0),0);
      return s + rSmv;
    }, 0);

    const totalParts = partGaps.length || 1;
    const confidence = Math.round(
      (FULL.length/totalParts)*100 * 0.7 +
      (PARTIAL.length/totalParts)*100 * 0.2 +
      (GAPS.length === 0 ? 30 : 0)
    );

    setSmvForecast({
      smvFull, smvPartial, smvReplacement,
      smvTotal: smvFull + smvPartial + smvReplacement,
      smvBestCase: smvFull + smvReplacement,
      confidence,
      fullCount: FULL.length, partialCount: PARTIAL.length, gapCount: GAPS.length,
      gaps: GAPS, partials: PARTIAL,
    });
  };

  // Styles
  const scoreColor = (s) => s >= 90 ? '#059669' : s >= 70 ? '#d97706' : s >= 50 ? '#7c3aed' : '#dc2626';
  const scoreBg    = (s) => s >= 90 ? '#ecfdf5' : s >= 70 ? '#fffbeb' : s >= 50 ? '#f5f3ff' : '#fef2f2';
  const statusColor = { matched:'#059669', partial:'#d97706', gap:'#dc2626', 'no-image':'#94a3b8' };
  const statusLabel = { matched:'Full Match', partial:'Partial Match', gap:'No Match', 'no-image':'No Image' };
  const bstyle = (bg='#2563eb',col='white') => ({ padding:'7px 18px', background:bg, color:col, border:'none', borderRadius:6, cursor:'pointer', fontWeight:700, fontSize:'0.82rem' });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'white', borderRadius:14, width:'100%', maxWidth:1050, maxHeight:'94vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,0.4)' }}>

        {/* Header */}
        <div style={{ padding:'14px 22px', background:'#0f172a', color:'white', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:800, fontSize:15 }}>Style Smart Match</div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
              {phase==='matching' ? 'Finding closest existing styles using pHash + HSV + edge analysis' :
               phase==='gaps'    ? 'Part-by-part gap analysis — identifying missing / mismatched parts' :
               'SMV forecast by confidence tier — 100% matches first, then best replacements'}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {/* Steps */}
            {['matching','gaps','smv'].map((s,i) => (
              <div key={s} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <div style={{ width:22, height:22, borderRadius:'50%', background: phase===s?'#3b82f6': styleScores.length&&s==='matching'?'#059669': selectedStyle&&s==='gaps'?'#059669': smvForecast&&s==='smv'?'#059669':'#334155', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700 }}>{i+1}</div>
                {i < 2 && <div style={{ width:20, height:1, background:'#334155' }}/>}
              </div>
            ))}
            <button onClick={onClose} style={{ ...bstyle('#1e293b'), marginLeft:12 }}>Close</button>
          </div>
        </div>

        <div style={{ flex:1, display:'flex', minHeight:0 }}>

          {/* Left: uploaded image + current phase info */}
          <div style={{ width:220, borderRight:'1px solid #f1f5f9', display:'flex', flexDirection:'column', flexShrink:0 }}>
            <div style={{ padding:14 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', marginBottom:6, textTransform:'uppercase' }}>Query Image</div>
              <img src={uploadedImage} alt="query" style={{ width:'100%', aspectRatio:'1', objectFit:'contain', border:'1px solid #e2e8f0', borderRadius:8, background:'#f8fafc' }}/>
            </div>
            {selectedStyle && (
              <div style={{ padding:14, borderTop:'1px solid #f1f5f9' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', marginBottom:6, textTransform:'uppercase' }}>Closest Style</div>
                <img src={selectedStyle.img||selectedStyle.image||selectedStyle.workstationData?.frontImage} alt="match" style={{ width:'100%', aspectRatio:'1', objectFit:'contain', border:'1px solid #e2e8f0', borderRadius:8, background:'#f8fafc' }}/>
                <div style={{ fontSize:11, fontWeight:700, marginTop:6, color:'#1e293b' }}>{selectedStyle.styleRef||selectedStyle.name}</div>
              </div>
            )}
          </div>

          {/* Right: main content */}
          <div style={{ flex:1, overflowY:'auto', padding:20 }}>

            {/* PHASE 1: Style Matching */}
            {phase === 'matching' && (
              <>
                {scanning ? (
                  <div style={{ textAlign:'center', padding:40 }}>
                    <div style={{ fontSize:32, marginBottom:12 }}>Scanning {allStyles.filter(s=>s.img||s.image).length} styles...</div>
                    <div style={{ background:'#f1f5f9', borderRadius:8, overflow:'hidden', height:8, margin:'0 auto', maxWidth:300 }}>
                      <div style={{ height:'100%', background:'#3b82f6', width:`${scanProgress}%`, transition:'width 0.2s' }}/>
                    </div>
                    <div style={{ marginTop:8, color:'#64748b', fontSize:12 }}>{scanProgress}% complete — pHash + HSV + edge analysis</div>
                  </div>
                ) : styleScores.length === 0 ? (
                  <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>
                    <div style={{ fontSize:48, marginBottom:12 }}>No styles with images found.</div>
                    <div>Add garment images to your styles to enable Smart Match.</div>
                    <button style={{ ...bstyle(), marginTop:16 }} onClick={() => { onSelectStyle(null); onClose(); }}>Start New Style</button>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ fontSize:13, color:'#64748b' }}>
                        Found <strong>{styleScores.length}</strong> styles with images — sorted by similarity
                      </div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>pHash 45% + Colour 30% + Edge 25%</div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:12 }}>
                      {styleScores.map(({ style, score, breakdown }) => (
                        <div key={style.id} onClick={() => analyseGaps(style)}
                          style={{ border:`2px solid ${selectedStyle?.id===style.id?'#3b82f6':'#e2e8f0'}`, borderRadius:10, cursor:'pointer', overflow:'hidden', background:'white', transition:'all 0.15s' }}>
                          <div style={{ position:'relative' }}>
                            <img src={style.img||style.image||style.workstationData?.frontImage} alt={style.styleRef} style={{ width:'100%', height:130, objectFit:'contain', background:'#f8fafc' }}/>
                            <div style={{ position:'absolute', top:6, right:6, background:scoreColor(score), color:'white', fontWeight:800, fontSize:12, padding:'2px 8px', borderRadius:10 }}>{score}%</div>
                          </div>
                          <div style={{ padding:10 }}>
                            <div style={{ fontWeight:700, fontSize:12, color:'#1e293b', marginBottom:4 }}>{style.styleRef||style.name}</div>
                            <div style={{ fontSize:10, color:'#64748b', marginBottom:6 }}>{style.brand} {style.season && '· '+style.season}</div>
                            {/* Algorithm breakdown mini bars */}
                            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                              {[['Shape',breakdown.structural,'#3b82f6'],['Colour',breakdown.colour,'#f59e0b'],['Edge',breakdown.edge,'#8b5cf6']].map(([label,val,col]) => (
                                <div key={label} style={{ display:'flex', gap:5, alignItems:'center' }}>
                                  <span style={{ fontSize:9, color:'#94a3b8', width:38 }}>{label}</span>
                                  <div style={{ flex:1, height:4, background:'#f1f5f9', borderRadius:2 }}>
                                    <div style={{ height:'100%', width:`${val}%`, background:col, borderRadius:2 }}/>
                                  </div>
                                  <span style={{ fontSize:9, color:col, width:24, textAlign:'right' }}>{val}%</span>
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop:8, fontSize:10, color:'#64748b' }}>{(style.workstationData?.items||[]).length} parts on canvas</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* PHASE 2: Part Gap Analysis */}
            {phase === 'gaps' && (
              <>
                {partGaps.length === 0 ? (
                  <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>Analysing parts... please wait</div>
                ) : (
                  <>
                    <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                      {[['Full Match (95-100%)','matched','#059669'],['Partial (60-94%)','partial','#d97706'],['No Match (<60%)','gap','#dc2626']].map(([label,status,col]) => {
                        const count = partGaps.filter(g=>g.status===status).length;
                        return <div key={status} style={{ padding:'6px 14px', background:col+'18', border:`1px solid ${col}44`, borderRadius:6, fontSize:12, color:col, fontWeight:700 }}>{count} {label}</div>;
                      })}
                      <button style={{ ...bstyle('#059669'), marginLeft:'auto' }} onClick={buildForecast}>
                        Generate SMV Forecast &rarr;
                      </button>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {partGaps.map((gap, gi) => (
                        <div key={gi} style={{ border:`1px solid ${statusColor[gap.status]}44`, borderLeft:`4px solid ${statusColor[gap.status]}`, borderRadius:8, padding:12, background:'white' }}>
                          <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                            {gap.cp.image && <img src={gap.cp.image} alt={gap.cp.name} style={{ width:56, height:56, objectFit:'contain', borderRadius:5, border:'1px solid #e2e8f0', background:'#f8fafc', flexShrink:0 }}/>}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                                <div style={{ fontWeight:700, fontSize:13 }}>{gap.cp.name}</div>
                                <span style={{ background:scoreColor(gap.score), color:'white', fontWeight:800, fontSize:11, padding:'2px 8px', borderRadius:10 }}>{gap.score}% — {statusLabel[gap.status]}</span>
                              </div>
                              <div style={{ fontSize:11, color:'#64748b', marginBottom:gap.replacements.length?6:0 }}>
                                {(gap.cp.processes||[]).length} processes · SMV {(gap.cp.processes||[]).reduce((s,p)=>s+(parseFloat(p.smvOverride||p.importedSMV)||0),0).toFixed(3)}
                              </div>
                              {/* Replacement parts */}
                              {gap.replacements.length > 0 && (
                                <div>
                                  <div style={{ fontSize:10, fontWeight:700, color:'#64748b', marginBottom:4 }}>Best library replacements:</div>
                                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                                    {gap.replacements.slice(0,3).map(({part:rp, score:rs}) => (
                                      <div key={rp.id} style={{ display:'flex', gap:5, alignItems:'center', padding:'3px 8px', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:5, fontSize:11 }}>
                                        {rp.image && <img src={rp.image} alt={rp.name} style={{ width:20, height:20, objectFit:'contain', borderRadius:2 }}/>}
                                        <span style={{ color:'#1e293b' }}>{rp.name}</span>
                                        <span style={{ background:scoreColor(rs), color:'white', fontSize:9, fontWeight:700, padding:'0 4px', borderRadius:8 }}>{rs}%</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* PHASE 3: SMV Forecast */}
            {phase === 'smv' && smvForecast && (
              <>
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
                    {[
                      ['Tier 1: 100% Matched Parts', smvForecast.smvFull, '#059669', `${smvForecast.fullCount} parts — fully confirmed`],
                      ['Tier 2: Partial Matches',    smvForecast.smvPartial, '#d97706', `${smvForecast.partialCount} parts — approximate`],
                      ['Tier 3: Best Replacements',  smvForecast.smvReplacement, '#7c3aed', `${smvForecast.gapCount} parts — from library search`],
                    ].map(([label,val,col,sub]) => (
                      <div key={label} style={{ border:`1px solid ${col}44`, borderRadius:10, padding:14, background:col+'08' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:col, marginBottom:4 }}>{label}</div>
                        <div style={{ fontSize:26, fontWeight:900, color:col }}>{val.toFixed(3)}</div>
                        <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>min {sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Total forecast */}
                  <div style={{ background:'#0f172a', color:'white', borderRadius:12, padding:20, display:'flex', gap:20, alignItems:'center' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4 }}>TOTAL SMV FORECAST</div>
                      <div style={{ fontSize:38, fontWeight:900, color:'white' }}>{smvForecast.smvTotal.toFixed(3)} <span style={{ fontSize:16, color:'#94a3b8' }}>min</span></div>
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Best-case (100% + replacements): {smvForecast.smvBestCase.toFixed(3)} min</div>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4 }}>FORECAST CONFIDENCE</div>
                      <div style={{ fontSize:42, fontWeight:900, color: smvForecast.confidence>=80?'#4ade80':smvForecast.confidence>=60?'#fbbf24':'#f87171' }}>{smvForecast.confidence}%</div>
                    </div>
                  </div>

                  {/* Confidence explanation */}
                  <div style={{ marginTop:12, padding:'10px 14px', background:'#f8fafc', borderRadius:8, fontSize:11, color:'#64748b', lineHeight:1.6 }}>
                    <strong>How this was calculated:</strong> Tier 1 (100% structural + shape match) contributes 70% weight to confidence. Tier 2 partial matches contribute 20%. Gaps filled by library parts reduce confidence proportionally. For production costing, use Tier 1+3 best-case. For sampling, add 15% buffer to total.
                  </div>
                </div>

                {/* Action: use this style as base */}
                <div style={{ display:'flex', gap:10 }}>
                  <button style={bstyle()} onClick={() => { onSelectStyle(selectedStyle); onClose(); }}>
                    Use this Style as Base &rarr; Open in Workstation
                  </button>
                  <button style={bstyle('#f1f5f9','#475569')} onClick={() => setPhase('gaps')}>&larr; Back to Gap Analysis</button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer navigation */}
        {!scanning && (
          <div style={{ padding:'10px 20px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, background:'#f8fafc' }}>
            <button style={bstyle('#f1f5f9','#475569')} onClick={onClose}>Cancel &mdash; Start Fresh</button>
            <div style={{ display:'flex', gap:8 }}>
              {phase !== 'matching' && <button style={bstyle('#f1f5f9','#475569')} onClick={() => setPhase(phase==='smv'?'gaps':'matching')}>&larr; Back</button>}
              {phase === 'matching' && styleScores[0] && !selectedStyle && (
                <button style={bstyle()} onClick={() => analyseGaps(styleScores[0].style)}>Auto-pick Top Match &rarr;</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
//  PART HOVER TOOLTIP
// -----------------------------------------------------------------------------
const PartHoverTooltip = ({ part, db, x, y }) => {
  const smv = (part.processes||[]).reduce((s,p)=>s+calcProcessSMV(p,db),0);
  return (
    <div style={{position:'fixed',left:x+14,top:y-10,background:'white',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'10px 12px',zIndex:99999,boxShadow:'0 4px 20px rgba(0,0,0,0.15)',minWidth:200,maxWidth:250,pointerEvents:'none'}}>
      {part.image&&<div style={{height:70,background:'#f5f6f8',borderRadius:'5px',marginBottom:'8px',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}><img src={part.image} style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}} alt=""/></div>}
      <div style={{fontWeight:'bold',fontSize:'0.82rem',marginBottom:'4px'}}>{part.name}</div>
      <div style={{fontSize:'0.72rem',color:'#676879',lineHeight:1.7}}>
        <div>Garment: <strong>{part.garmentType||'-'}</strong></div>
        <div>Fabric: <strong>{part.fabricType||'-'}</strong></div>
        <div>Processes: <strong>{(part.processes||[]).length}</strong></div>
        <div style={{color:'#059669',fontWeight:'bold',marginTop:'2px'}}>SMV: {smv.toFixed(3)} min</div>
      </div>
      <div style={{marginTop:'5px',fontSize:'0.62rem',color:'#0073ea',background:'#e8f0ff',padding:'2px 6px',borderRadius:'3px',textAlign:'center'}}>Click to add to canvas</div>
    </div>
  );
};

// -----------------------------------------------------------------------------
//  PROCESS VIEW MODAL
// -----------------------------------------------------------------------------
const ProcessViewModal = ({ row, db, onClose }) => (
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',justifyContent:'center',alignItems:'center',zIndex:999998}}>
    <div style={{background:'white',borderRadius:'12px',width:'640px',maxHeight:'86vh',overflow:'auto'}}>
      <div style={{padding:'14px 20px',background:'#323338',borderRadius:'12px 12px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><div style={{color:'white',fontWeight:'bold',fontSize:'0.95rem'}}>{row.processName}</div>
          <div style={{color:'#9699a6',fontSize:'0.75rem'}}>{row.partName} · {row.machine} · SMV: {row.smv.toFixed(4)} min</div></div>
        <button onClick={onClose} style={{background:'#e2445c',color:'white',border:'none',borderRadius:'6px',padding:'6px 12px',cursor:'pointer'}}>✕ Close</button>
      </div>
      <div style={{padding:'18px'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'16px'}}>
          {[['Workers',row.workers,'#0073ea'],['Hourly',row.hourlyTarget+' pcs','#7C3AED'],['Thread',row.threadCm+' cm','#d97706'],['SMV',row.smv.toFixed(4),'#059669']].map(([l,v,c])=>(
            <div key={l} style={{background:'#f5f6f8',borderRadius:'8px',padding:'10px',textAlign:'center',borderTop:'3px solid '+c}}>
              <div style={{fontSize:'0.6rem',color:'#676879',fontWeight:'bold',textTransform:'uppercase'}}>{l}</div>
              <div style={{fontSize:'1rem',fontWeight:'bold',color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
          <thead><tr style={{background:'#f5f6f8'}}>
            {['Type','Machine','Length','Freq','Stop%','Fab%','Basic(s)'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',color:'#676879',fontWeight:'bold'}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(row.procObj?.ops||[]).map((op,oi)=>{
              const mc=(db.machines||[]).find(m=>m.id==op.mcId);
              const mn=(db.manualCodes||[]).find(m=>m.id==op.refId);
              return <tr key={oi} style={{borderBottom:'1px solid #f0f1f4'}}>
                <td style={{padding:'5px 8px'}}>{op.type}</td>
                <td style={{padding:'5px 8px'}}>{op.type==='machine'?mc?.code:(mn?.code||'-')}</td>
                <td style={{padding:'5px 8px',textAlign:'right'}}>{op.length||'-'}</td>
                <td style={{padding:'5px 8px',textAlign:'right'}}>{op.frequency||1}</td>
                <td style={{padding:'5px 8px',textAlign:'right'}}>{op.stoppage||0}%</td>
                <td style={{padding:'5px 8px',textAlign:'right'}}>{op.fabric||0}%</td>
                <td style={{padding:'5px 8px',textAlign:'right',fontWeight:'bold'}}>{calculateBasicTimeSecs(op,db.machines,db.manualCodes).toFixed(3)}</td>
              </tr>;
            })}
          </tbody>
        </table>
        <div style={{marginTop:'10px',fontSize:'0.72rem',color:'#676879'}}>Contingency 3% · P&F 11% · Bundle 0.09 min</div>
      </div>
    </div>
  </div>
);

// -----------------------------------------------------------------------------
//  LAYOUT CHART — Assembly line visual, proper appearance
// -----------------------------------------------------------------------------
const MACHINE_ICONS = {
  'OL':'⚙️','OVERLOCK':'⚙️','FL':'🔄','FLATLOCK':'🔄','SN':'🪡','SNLS':'🪡',
  'DN':'🧵','BH':'🕳️','BS':'🔘','BAR':'📌','KM':'🤝','MANUAL':'✋','MAN':'✋','DEFAULT':'⚙️'
};
const getMachineIcon = (code) => {
  const u=(code||'').toUpperCase();
  for(const[k,v] of Object.entries(MACHINE_ICONS)) if(k!=='DEFAULT'&&u.includes(k)) return v;
  return MACHINE_ICONS.DEFAULT;
};

const MACHINE_PALETTE = {
  'OL':'#dbeafe','OVERLOCK':'#dbeafe','FL':'#d1fae5','FLATLOCK':'#d1fae5',
  'SN':'#fce7f3','SNLS':'#fce7f3','DN':'#fef3c7','BH':'#ede9fe',
  'BS':'#ffedd5','BAR':'#f1f5f9','KM':'#ecfdf5','MAN':'#f8fafc','DEFAULT':'#f1f5f9'
};
const getMachineBg = (code) => {
  const u=(code||'').toUpperCase();
  for(const[k,v] of Object.entries(MACHINE_PALETTE)) if(k!=='DEFAULT'&&u.includes(k)) return v;
  return MACHINE_PALETTE.DEFAULT;
};

const LayoutChart = ({ obSequence, db, compact=false }) => {
  const [autoSeq, setAutoSeq] = useState(true);
  const [view, setView] = useState('line'); // 'line' | 'grid'

  const totalSMVAll = obSequence.reduce((s,p)=>s+calcProcessSMV(p,db),0);
  const pitchTime = obSequence.length>0 ? totalSMVAll/obSequence.length : 1;

  const displaySeq = autoSeq
    ? [...obSequence].sort((a,b)=>{
        // 1. Group by part (part completion = less transportation)
        if(a.partName!==b.partName) return a.partName.localeCompare(b.partName);
        // 2. Within part: group same machine type together
        const mcA=(db.machines||[]).find(m=>a.ops?.some(op=>op.mcId==m.id))?.code||'MAN';
        const mcB=(db.machines||[]).find(m=>b.ops?.some(op=>op.mcId==m.id))?.code||'MAN';
        return mcA.localeCompare(mcB);
      })
    : obSequence;

  if(displaySeq.length===0) return (
    <div style={{textAlign:'center',padding:'30px 20px',color:'#9699a6',fontSize:'0.8rem'}}>
      <Layout size={32} color="#c5c7d4" style={{display:'block',margin:'0 auto 8px'}}/>
      No processes yet. Add parts with processes to the canvas first.
    </div>
  );

  const machineGroups = {};
  displaySeq.forEach(p=>{
    const mc=(db.machines||[]).find(m=>p.ops?.some(op=>op.mcId==m.id))?.code||'MAN';
    if(!machineGroups[mc]) machineGroups[mc]=[];
    machineGroups[mc].push(p);
  });

  // Assembly line: rows of 8, connected with arrows
  const rowSize = compact ? 5 : 8;
  const rows = [];
  for(let i=0;i<displaySeq.length;i+=rowSize) rows.push(displaySeq.slice(i,i+rowSize));

  return (
    <div style={{fontFamily:'sans-serif'}}>
      {/* Controls */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:compact?'8px':'14px',flexWrap:'wrap',gap:'6px'}}>
        <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
          <button onClick={()=>setView('line')} style={{padding:'4px 10px',background:view==='line'?'#0073ea':'#f0f1f4',color:view==='line'?'white':'#676879',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'0.72rem',fontWeight:'bold'}}>Assembly Line</button>
          <button onClick={()=>setView('grid')} style={{padding:'4px 10px',background:view==='grid'?'#0073ea':'#f0f1f4',color:view==='grid'?'white':'#676879',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'0.72rem',fontWeight:'bold'}}>Grid</button>
        </div>
        <label style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'0.72rem',color:'#676879',cursor:'pointer'}}>
          <input type="checkbox" checked={autoSeq} onChange={e=>setAutoSeq(e.target.checked)}/>
          Auto-sequence (part completion + machine grouping)
        </label>
      </div>

      {/* KPI strip */}
      {!compact&&<div style={{display:'flex',gap:'12px',marginBottom:'14px',flexWrap:'wrap'}}>
        {[['Total SMV',totalSMVAll.toFixed(3)+' min'],['Pitch Time',pitchTime.toFixed(3)+' min'],
          ['Workstations',displaySeq.length],['Machine Types',Object.keys(machineGroups).length]
        ].map(([k,v])=>(
          <div key={k} style={{background:'white',border:'1px solid #e6e9ef',borderRadius:'6px',padding:'6px 12px',textAlign:'center'}}>
            <div style={{fontSize:'0.6rem',color:'#676879',fontWeight:'bold',textTransform:'uppercase'}}>{k}</div>
            <div style={{fontSize:'0.85rem',fontWeight:'bold',color:'#323338'}}>{v}</div>
          </div>
        ))}
      </div>}

      {view==='line' ? (
        /* Assembly line view — rows of stations connected by arrows */
        <div>
          {rows.map((row,ri)=>(
            <div key={ri}>
              {/* Row direction indicator */}
              {!compact&&<div style={{fontSize:'0.62rem',color:'#9699a6',marginBottom:'6px',display:'flex',alignItems:'center',gap:'4px'}}>
                <span>Row {ri+1}</span>
                <span style={{fontSize:'0.55rem'}}>{ri%2===0?'→':'←'}</span>
              </div>}
              <div style={{display:'flex',alignItems:'stretch',gap:0,marginBottom:compact?'8px':'12px',
                flexDirection: ri%2===0?'row':'row-reverse' /* Snake layout */}}>
                {row.map((p,i)=>{
                  const globalIdx=ri*rowSize+i;
                  const mc=(db.machines||[]).find(m=>p.ops?.some(op=>op.mcId==m.id))?.code||'MAN';
                  const smv=calcProcessSMV(p,db);
                  const workers=parseFloat((smv/Math.max(pitchTime,0.01)).toFixed(1));
                  const isBottleneck=smv>pitchTime*1.3;
                  const icon=getMachineIcon(mc);
                  const bg=getMachineBg(mc);
                  const isLast=i===row.length-1;
                  const isLastRow=ri===rows.length-1;
                  return (
                    <React.Fragment key={i}>
                      {/* Station card */}
                      <div style={{
                        width:compact?'110px':'130px', minHeight:compact?'80px':'100px',
                        border:'2px solid '+(isBottleneck?'#e2445c':'#c5c7d4'),
                        borderRadius:'8px', padding:'7px 7px 6px',
                        background:bg, position:'relative',
                        boxShadow:isBottleneck?'0 0 0 2px #fecaca':'0 1px 3px rgba(0,0,0,0.08)',
                        flexShrink:0
                      }}>
                        {/* Station number */}
                        <div style={{position:'absolute',top:4,left:6,fontSize:'0.58rem',color:'#9699a6',fontWeight:'bold'}}>#{globalIdx+1}</div>
                        {/* Bottleneck flag */}
                        {isBottleneck&&<div style={{position:'absolute',top:3,right:4,fontSize:'0.55rem',background:'#e2445c',color:'white',padding:'1px 4px',borderRadius:'3px',fontWeight:'bold'}}>⚠ HIGH</div>}
                        {/* Machine icon + code */}
                        <div style={{display:'flex',alignItems:'center',gap:'4px',marginBottom:'4px',marginTop:'8px'}}>
                          <span style={{fontSize:'0.85rem'}}>{icon}</span>
                          <span style={{fontSize:'0.65rem',fontWeight:'bold',color:'#323338',background:'rgba(255,255,255,0.7)',padding:'1px 5px',borderRadius:'3px'}}>{mc.substring(0,6)}</span>
                        </div>
                        {/* Process name */}
                        <div style={{fontSize:'0.65rem',fontWeight:'600',lineHeight:1.25,marginBottom:'3px',overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{p.name}</div>
                        {/* Part name */}
                        {!compact&&<div style={{fontSize:'0.58rem',color:'#676879',marginBottom:'4px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.partName}</div>}
                        {/* SMV + Workers bar */}
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'auto'}}>
                          <span style={{fontSize:'0.62rem',color:'#059669',fontWeight:'bold'}}>{smv.toFixed(2)}m</span>
                          <span style={{fontSize:'0.6rem',background:workers>1?'#fef3c7':'#dcfce7',color:workers>1?'#92400e':'#166534',padding:'1px 5px',borderRadius:'3px',fontWeight:'bold'}}>W:{workers}</span>
                        </div>
                        {/* SMV bar (visual utilisation vs pitch) */}
                        <div style={{marginTop:'4px',height:'3px',background:'#e5e7eb',borderRadius:'2px',overflow:'hidden'}}>
                          <div style={{height:'100%',width:Math.min(100,(smv/pitchTime)*100)+'%',background:isBottleneck?'#e2445c':'#0073ea',borderRadius:'2px'}}/>
                        </div>
                      </div>
                      {/* Arrow connector (not after last in row) */}
                      {!isLast&&<div style={{display:'flex',alignItems:'center',color:'#9699a6',fontSize:'0.7rem',flexShrink:0,padding:'0 2px'}}>→</div>}
                      {/* Row-end down arrow */}
                      {isLast&&!isLastRow&&<div style={{display:'flex',alignItems:'center',color:'#0073ea',fontSize:'1rem',flexShrink:0,padding:'0 4px'}}>↓</div>}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
          {/* Machine legend */}
          {!compact&&Object.keys(machineGroups).length>0&&(
            <div style={{marginTop:'14px',padding:'10px',background:'white',border:'1px solid #e6e9ef',borderRadius:'6px'}}>
              <div style={{fontSize:'0.65rem',fontWeight:'bold',color:'#676879',textTransform:'uppercase',marginBottom:'7px'}}>Machine Legend</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
                {Object.entries(machineGroups).map(([mc,procs])=>(
                  <div key={mc} style={{display:'flex',alignItems:'center',gap:'5px',background:getMachineBg(mc),border:'1px solid #e6e9ef',borderRadius:'5px',padding:'4px 8px',fontSize:'0.68rem'}}>
                    <span>{getMachineIcon(mc)}</span>
                    <strong>{mc}</strong>
                    <span style={{color:'#676879'}}>{procs.length} ops</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Grid view */
        <div style={{display:'grid',gridTemplateColumns:compact?'repeat(3,1fr)':'repeat(auto-fill,minmax(130px,1fr))',gap:'8px'}}>
          {displaySeq.map((p,i)=>{
            const mc=(db.machines||[]).find(m=>p.ops?.some(op=>op.mcId==m.id))?.code||'MAN';
            const smv=calcProcessSMV(p,db);
            const workers=parseFloat((smv/Math.max(pitchTime,0.01)).toFixed(1));
            return (
              <div key={i} style={{border:'2px solid #c5c7d4',borderRadius:'8px',padding:'7px',background:getMachineBg(mc),fontSize:'0.65rem',position:'relative'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                  <span style={{color:'#9699a6',fontWeight:'bold'}}>#{i+1}</span>
                  <span style={{fontSize:'0.85rem'}}>{getMachineIcon(mc)}</span>
                </div>
                <div style={{fontWeight:'600',marginBottom:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                <div style={{color:'#676879',fontSize:'0.58rem',marginBottom:'4px'}}>{p.partName}</div>
                <div style={{display:'flex',justifyContent:'space-between'}}>
                  <span style={{color:'#059669',fontWeight:'bold'}}>{smv.toFixed(2)}m</span>
                  <span style={{fontSize:'0.6rem',background:workers>1?'#fef3c7':'#dcfce7',color:workers>1?'#92400e':'#166534',padding:'1px 4px',borderRadius:'3px',fontWeight:'bold'}}>W:{workers}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// =============================================================================
//  TECHPACK MODAL — Full garment tech pack builder
// =============================================================================
const TechpackModal = ({ style, db, savedFront, savedBack, obSequence, onClose, doSave }) => {
  const [activeTab, setActiveTab] = useState('cover');  // cover|specs|bom|annotations
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // ── Cover / Identity
  const [designerName, setDesignerName] = useState(style?.designerName || '');
  const [season, setSeason] = useState(style?.season || '');
  const [brand, setBrand] = useState(style?.brand || '');
  const [styleRef, setStyleRef] = useState(style?.styleRef || '');
  const [description, setDescription] = useState(style?.description || '');
  const [colourways, setColourways] = useState(style?.colourways || [{ name: 'Colour 1', hex: '#3b82f6' }]);

  // ── Specs table
  const [specRows, setSpecRows] = useState(() => {
    const existing = style?.techpackSpecs || [];
    if (existing.length > 0) return existing;
    return [
      { point: 'Chest', s: '', m: '', l: '', xl: '', tol: '±1', how: 'Measure across chest 1" below armhole' },
      { point: 'Waist', s: '', m: '', l: '', xl: '', tol: '±1', how: '' },
      { point: 'Hip', s: '', m: '', l: '', xl: '', tol: '±1', how: '' },
      { point: 'Length (CB)', s: '', m: '', l: '', xl: '', tol: '±1', how: 'Centre back from HPS' },
      { point: 'Sleeve Length', s: '', m: '', l: '', xl: '', tol: '±0.5', how: '' },
      { point: 'Sleeve Opening', s: '', m: '', l: '', xl: '', tol: '±0.5', how: '' },
    ];
  });
  const addSpecRow = () => setSpecRows(r => [...r, { point: '', s: '', m: '', l: '', xl: '', tol: '±1', how: '' }]);
  const updateSpec = (i, k, v) => setSpecRows(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeSpec = (i) => setSpecRows(r => r.filter((_, idx) => idx !== i));

  // ── BoM (pulls from style registry if available, else blank)
  const [bomRows, setBomRows] = useState(() => {
    const existing = style?.bom || style?.workstationData?.bom || [];
    if (existing.length > 0) return existing;
    return [
      { item: 'Main Fabric', desc: style?.fabric || '', supplier: '', colour: '', composition: '', gsm: '', uom: 'm', consumption: '', rate: '' },
      { item: 'Lining', desc: '', supplier: '', colour: '', composition: '', gsm: '', uom: 'm', consumption: '', rate: '' },
      { item: 'Main Label', desc: '', supplier: '', colour: '', composition: '', gsm: '', uom: 'pcs', consumption: '1', rate: '' },
      { item: 'Size Label', desc: '', supplier: '', colour: '', composition: '', gsm: '', uom: 'pcs', consumption: '1', rate: '' },
      { item: 'Care Label', desc: '', supplier: '', colour: '', composition: '', gsm: '', uom: 'pcs', consumption: '1', rate: '' },
      { item: 'Thread', desc: '', supplier: '', colour: '', composition: '', gsm: '', uom: 'cones', consumption: '', rate: '' },
    ];
  });
  const addBomRow = () => setBomRows(r => [...r, { item: '', desc: '', supplier: '', colour: '', composition: '', gsm: '', uom: 'm', consumption: '', rate: '' }]);
  const updateBom = (i, k, v) => setBomRows(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeBom = (i) => setBomRows(r => r.filter((_, idx) => idx !== i));

  // ── Annotations on front/back image
  const [annotations, setAnnotations] = useState(style?.techpackAnnotations || []);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [annotationTool, setAnnotationTool] = useState('arrow'); // arrow|text|callout
  const [annotationText, setAnnotationText] = useState('');
  const [annotationImage, setAnnotationImage] = useState('front');
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [isDraggingAnnotation, setIsDraggingAnnotation] = useState(false);

  const addAnnotation = (e, view) => {
    if (annotationTool === 'none') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width * 100);
    const y = ((e.clientY - rect.top) / rect.height * 100);
    const newA = {
      id: Date.now(), x, y, type: annotationTool,
      text: annotationText || (annotationTool === 'text' ? 'Label' : 'Feature'),
      view, color: '#e2445c',
      toX: x + 8, toY: y - 8,  // arrow end (relative %)
    };
    setAnnotations(a => [...a, newA]);
    setSelectedAnnotation(newA.id);
  };

  const updateAnnotation = (id, key, val) => setAnnotations(a => a.map(ann => ann.id === id ? { ...ann, [key]: val } : ann));
  const removeAnnotation = (id) => { setAnnotations(a => a.filter(ann => ann.id !== id)); setSelectedAnnotation(null); };

  // ── Save techpack to style
  const saveTechpack = async () => {
    setIsSaving(true);
    const techpackData = { designerName, season, brand, styleRef, description, colourways, techpackSpecs: specRows, bom: bomRows, techpackAnnotations: annotations, generatedAt: new Date().toISOString() };
    try {
      await doSave('workstation', { ...style, techpack: techpackData });
      setSaveMsg('Saved!'); setTimeout(() => setSaveMsg(''), 2000);
    } catch { setSaveMsg('Error saving'); }
    setIsSaving(false);
  };

  // ── Print techpack
  const printTechpack = () => {
    const w = window.open('', '_blank');
    const smv = obSequence.reduce((s, p) => {
      const basicSecs = (p.ops || []).reduce((ss, op) => {
        if (op.smvOverride) return ss + op.smvOverride * 60;
        return ss;
      }, 0);
      return s + basicSecs / 60 + 0.09;
    }, 0);
    w.document.write(`<!DOCTYPE html><html><head><title>Techpack - ${styleRef}</title>
    <style>
      *{box-sizing:border-box;} body{font-family:Arial,sans-serif;font-size:10px;margin:15mm;color:#1e293b;}
      h1{font-size:18px;margin:0 0 4px;} h2{font-size:13px;margin:16px 0 6px;border-bottom:2px solid #0073ea;padding-bottom:4px;color:#0073ea;}
      .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:3px solid #323338;}
      .meta{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:12px;}
      .meta-item{background:#f5f6f8;padding:6px 8px;border-radius:4px;}
      .meta-label{font-size:8px;color:#676879;font-weight:bold;text-transform:uppercase;}
      .meta-value{font-size:11px;font-weight:bold;margin-top:2px;}
      .images{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:12px;}
      .img-box{border:1px solid #e6e9ef;border-radius:6px;padding:8px;text-align:center;}
      .img-box img{max-width:100%;max-height:220px;object-fit:contain;}
      .img-label{font-weight:bold;font-size:9px;color:#676879;margin-bottom:6px;text-transform:uppercase;}
      table{width:100%;border-collapse:collapse;margin-bottom:12px;font-size:8.5px;}
      th{background:#323338;color:white;padding:5px 6px;text-align:left;font-weight:bold;}
      td{padding:4px 6px;border-bottom:1px solid #e6e9ef;}
      tr:nth-child(even) td{background:#f9fafb;}
      .footer{margin-top:16px;padding-top:8px;border-top:1px solid #e6e9ef;display:flex;justify-content:space-between;font-size:8px;color:#9699a6;}
      .colour-swatch{display:inline-block;width:12px;height:12px;border-radius:3px;border:1px solid #ddd;margin-right:4px;vertical-align:middle;}
      @media print{@page{size:A4;margin:10mm;}}
    </style></head><body>
    <div class="header">
      <div>
        <h1>${styleRef}</h1>
        <div style="color:#676879;">${brand} &nbsp;·&nbsp; ${season} &nbsp;·&nbsp; Designer: ${designerName||'—'}</div>
        <div style="margin-top:4px;font-size:9px;color:#323338;">${description||''}</div>
      </div>
      <div style="text-align:right;font-size:8px;color:#9699a6;">
        <div>Generated: ${new Date().toLocaleDateString()}</div>
        <div>Total SMV: <strong>${smv.toFixed(3)} min</strong></div>
      </div>
    </div>
    <div class="meta">
      ${[['Total Qty', style?.totalQty||'—'],['Planned Eff', style?.plannedEfficiency?style.plannedEfficiency+'%':'—'],['Total SMV', smv.toFixed(3)+' min'],['Processes', obSequence.length]].map(([k,v])=>`<div class="meta-item"><div class="meta-label">${k}</div><div class="meta-value">${v}</div></div>`).join('')}
    </div>
    ${(savedFront||savedBack)?`<div class="images">
      ${savedFront?`<div class="img-box"><div class="img-label">Front View</div><img src="${savedFront}" alt="Front"/></div>`:''}
      ${savedBack?`<div class="img-box"><div class="img-label">Back View</div><img src="${savedBack}" alt="Back"/></div>`:''}
    </div>`:''}
    <h2>Colour Ways</h2>
    <div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:12px;">
      ${colourways.map(c=>`<div style="display:flex;align-items:center;gap:4px;background:#f5f6f8;padding:4px 8px;border-radius:4px;">
        <span class="colour-swatch" style="background:${c.hex}"></span><span>${c.name}</span>
      </div>`).join('')}
    </div>
    <h2>Measurement Spec Sheet</h2>
    <table>
      <thead><tr><th>Point of Measure</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>Tolerance</th><th>How to Measure</th></tr></thead>
      <tbody>${specRows.map(r=>`<tr><td><strong>${r.point}</strong></td><td>${r.s}</td><td>${r.m}</td><td>${r.l}</td><td>${r.xl}</td><td>${r.tol}</td><td>${r.how}</td></tr>`).join('')}</tbody>
    </table>
    <h2>Bill of Materials (BoM)</h2>
    <table>
      <thead><tr><th>Item</th><th>Description</th><th>Supplier</th><th>Colour</th><th>Composition</th><th>GSM</th><th>UOM</th><th>Consumption</th><th>Rate</th></tr></thead>
      <tbody>${bomRows.map(r=>`<tr><td><strong>${r.item}</strong></td><td>${r.desc}</td><td>${r.supplier}</td><td>${r.colour}</td><td>${r.composition}</td><td>${r.gsm}</td><td>${r.uom}</td><td>${r.consumption}</td><td>${r.rate}</td></tr>`).join('')}</tbody>
    </table>
    ${obSequence.length>0?`<h2>Operation Bulletin Summary</h2>
    <table>
      <thead><tr><th>SL</th><th>Part</th><th>Operation</th><th>Machine</th><th>SMV (min)</th></tr></thead>
      <tbody>${obSequence.map((p,i)=>{const smvP=((p.ops||[]).reduce((s,op)=>s+(op.smvOverride||0)*60,0)/60+0.09);return`<tr><td>${i+1}</td><td>${p.partName||''}</td><td>${p.name||''}</td><td>${(db.machines||[]).find(m=>p.ops?.some(op=>op.mcId==m.id))?.code||'—'}</td><td>${smvP.toFixed(4)}</td></tr>`;}).join('')}</tbody>
    </table>`:''}
    <div class="footer">
      <div>${brand} | ${styleRef} | Confidential</div>
      <div>Page 1 of 1</div>
    </div>
    <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
    </body></html>`);
    w.document.close();
  };

  const tabStyle = (t) => ({ flex:1, padding:'10px 4px', background:activeTab===t?'#e8f0ff':'white', border:'none', borderBottom:activeTab===t?'2px solid #0073ea':'2px solid transparent', fontWeight:'bold', fontSize:'0.7rem', textTransform:'uppercase', cursor:'pointer', color:activeTab===t?'#0073ea':'#676879' });
  const inputSt = { padding:'4px 6px', border:'1px solid #e6e9ef', borderRadius:'4px', fontSize:'0.8rem', width:'100%' };
  const labelSt = { fontSize:'0.68rem', color:'#676879', display:'block', marginBottom:'3px', fontWeight:'bold' };

  return ReactDOM.createPortal(
    <div style={{position:'fixed',inset:0,background:'white',zIndex:99998,display:'flex',flexDirection:'column'}}>
      {/* Header */}
      <div style={{padding:'10px 20px',background:'#323338',color:'white',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
        <div>
          <h3 style={{margin:0,fontSize:'1rem'}}>📋 TECHPACK BUILDER</h3>
          <div style={{fontSize:'0.72rem',color:'#9699a6',marginTop:'1px'}}>{styleRef} · {brand} · {season}</div>
        </div>
        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
          {saveMsg&&<span style={{fontSize:'0.78rem',color:'#4ade80',fontWeight:'bold'}}>{saveMsg}</span>}
          <button onClick={saveTechpack} disabled={isSaving} style={{padding:'6px 14px',background:'#059669',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'0.8rem',fontWeight:'bold'}}>
            {isSaving?'Saving…':'💾 Save'}
          </button>
          <button onClick={printTechpack} style={{padding:'6px 14px',background:'#dc2626',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'0.8rem',fontWeight:'bold'}}>
            🖨 Print / PDF
          </button>
          <button onClick={onClose} style={{padding:'6px 14px',background:'#676879',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}>
            <ChevronLeft size={14}/> Back to Workstation
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',borderBottom:'1px solid #e6e9ef',flexShrink:0}}>
        {[['cover','Cover & Identity'],['annotations','Style Images & Callouts'],['specs','Measurement Specs'],['bom','Bill of Materials (BoM)']].map(([k,lbl])=>(
          <button key={k} onClick={()=>setActiveTab(k)} style={tabStyle(k)}>{lbl}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{flex:1,overflow:'auto',padding:'20px'}}>

        {/* ── COVER TAB ── */}
        {activeTab==='cover'&&(
          <div style={{maxWidth:'760px',margin:'0 auto'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'20px'}}>
              {[['Style Reference',styleRef,setStyleRef],['Brand',brand,setBrand],['Season',season,setSeason],['Designer Name',designerName,setDesignerName]].map(([lbl,val,setter])=>(
                <div key={lbl}><label style={labelSt}>{lbl}</label><input style={inputSt} value={val} onChange={e=>setter(e.target.value)}/></div>
              ))}
              <div style={{gridColumn:'1/-1'}}>
                <label style={labelSt}>Style Description</label>
                <textarea style={{...inputSt,height:'60px',resize:'vertical'}} value={description} onChange={e=>setDescription(e.target.value)} placeholder="Describe the garment style, key design features…"/>
              </div>
            </div>
            {/* Colourways */}
            <div style={{marginBottom:'20px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                <div style={{fontWeight:'bold',fontSize:'0.85rem',color:'#323338'}}>Colour Ways</div>
                <button onClick={()=>setColourways(c=>[...c,{name:'New Colour',hex:'#94a3b8'}])} style={{padding:'4px 10px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #c5d9f5',borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem',fontWeight:'bold'}}>+ Add Colour</button>
              </div>
              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                {colourways.map((c,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:'6px',background:'#f5f6f8',padding:'6px 10px',borderRadius:'6px',border:'1px solid #e6e9ef'}}>
                    <input type="color" value={c.hex} onChange={e=>setColourways(cw=>cw.map((x,xi)=>xi===i?{...x,hex:e.target.value}:x))} style={{width:28,height:28,border:'none',borderRadius:'4px',cursor:'pointer',padding:0}}/>
                    <input value={c.name} onChange={e=>setColourways(cw=>cw.map((x,xi)=>xi===i?{...x,name:e.target.value}:x))} style={{border:'none',background:'transparent',fontSize:'0.8rem',width:'100px',outline:'none',fontWeight:'bold'}}/>
                    <button onClick={()=>setColourways(cw=>cw.filter((_,xi)=>xi!==i))} style={{background:'none',border:'none',cursor:'pointer',color:'#e2445c',fontSize:'0.8rem',padding:0}}>✕</button>
                  </div>
                ))}
              </div>
            </div>
            {/* Preview card */}
            <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'10px',padding:'20px',textAlign:'center'}}>
              <div style={{fontSize:'0.7rem',color:'#676879',fontWeight:'bold',textTransform:'uppercase',marginBottom:'8px'}}>Techpack Cover Preview</div>
              <div style={{background:'white',border:'2px solid #323338',borderRadius:'8px',padding:'20px',maxWidth:'360px',margin:'0 auto'}}>
                <div style={{fontWeight:'bold',fontSize:'1.1rem',color:'#323338',marginBottom:'2px'}}>{styleRef||'Style Reference'}</div>
                <div style={{color:'#676879',fontSize:'0.8rem',marginBottom:'12px'}}>{brand||'Brand'} · {season||'Season'}</div>
                <div style={{display:'flex',gap:'8px',justifyContent:'center',marginBottom:'12px'}}>
                  {savedFront&&<img src={savedFront} style={{height:120,objectFit:'contain',border:'1px solid #ddd',borderRadius:'4px'}} alt="front"/>}
                  {savedBack&&<img src={savedBack} style={{height:120,objectFit:'contain',border:'1px solid #ddd',borderRadius:'4px'}} alt="back"/>}
                  {!savedFront&&!savedBack&&<div style={{height:120,width:160,background:'#f0f1f4',borderRadius:'6px',display:'flex',alignItems:'center',justifyContent:'center',color:'#9699a6',fontSize:'0.75rem'}}>Save front/back images first</div>}
                </div>
                <div style={{display:'flex',gap:'6px',justifyContent:'center',flexWrap:'wrap'}}>
                  {colourways.map((c,i)=><div key={i} style={{display:'flex',alignItems:'center',gap:'3px',fontSize:'0.7rem'}}><div style={{width:10,height:10,background:c.hex,borderRadius:'2px',border:'1px solid #ddd'}}/>{c.name}</div>)}
                </div>
                <div style={{marginTop:'10px',fontSize:'0.7rem',color:'#9699a6'}}>Designer: {designerName||'—'}</div>
              </div>
            </div>
          </div>
        )}

        {/* ── ANNOTATIONS TAB ── */}
        {activeTab==='annotations'&&(
          <div>
            <div style={{display:'flex',gap:'12px',marginBottom:'14px',alignItems:'center',flexWrap:'wrap'}}>
              <div style={{fontWeight:'bold',color:'#323338',fontSize:'0.85rem'}}>Annotation Tool:</div>
              {[['none','✋ Move'],['arrow','↗ Arrow + Label'],['text','T Text Label'],['callout','💬 Callout']].map(([t,lbl])=>(
                <button key={t} onClick={()=>setAnnotationTool(t)} style={{padding:'5px 12px',background:annotationTool===t?'#0073ea':'#f0f1f4',color:annotationTool===t?'white':'#676879',border:'none',borderRadius:'5px',cursor:'pointer',fontSize:'0.78rem',fontWeight:'bold'}}>{lbl}</button>
              ))}
              <input value={annotationText} onChange={e=>setAnnotationText(e.target.value)} placeholder="Label text…" style={{padding:'5px 10px',border:'1px solid #e6e9ef',borderRadius:'5px',fontSize:'0.78rem',minWidth:'160px'}}/>
              <div style={{display:'flex',border:'1px solid #ddd',borderRadius:'5px',overflow:'hidden'}}>
                {['front','back'].map(v=><button key={v} onClick={()=>setAnnotationImage(v)} style={{padding:'5px 12px',background:annotationImage===v?'#323338':'white',color:annotationImage===v?'white':'#676879',border:'none',cursor:'pointer',fontSize:'0.75rem',fontWeight:'bold',textTransform:'uppercase'}}>{v}</button>)}
              </div>
              {selectedAnnotation&&<button onClick={()=>removeAnnotation(selectedAnnotation)} style={{padding:'5px 10px',background:'#fef2f2',color:'#e2445c',border:'1px solid #fecaca',borderRadius:'5px',cursor:'pointer',fontSize:'0.75rem',fontWeight:'bold'}}>🗑 Delete Selected</button>}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
              {[['front',savedFront],['back',savedBack]].map(([view,img])=>(
                <div key={view}>
                  <div style={{fontWeight:'bold',fontSize:'0.78rem',color:'#676879',textTransform:'uppercase',marginBottom:'6px',textAlign:'center'}}>{view} view</div>
                  <div style={{position:'relative',border:'2px solid #e6e9ef',borderRadius:'8px',overflow:'hidden',background:'#f5f6f8',minHeight:'280px',cursor:annotationTool!=='none'?'crosshair':'default'}}
                    onClick={e=>annotationImage===view&&annotationTool!=='none'&&addAnnotation(e,view)}>
                    {img
                      ? <img src={img} style={{width:'100%',display:'block',objectFit:'contain',maxHeight:'380px'}} alt={view} draggable={false}/>
                      : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'280px',color:'#9699a6',fontSize:'0.82rem',flexDirection:'column',gap:'8px'}}>
                          <ImageIcon size={28} color="#c5c7d4"/>
                          Save {view} view to enable annotations
                        </div>
                    }
                    {/* Annotations overlay */}
                    {annotations.filter(a=>a.view===view).map(ann=>(
                      <div key={ann.id} onClick={e=>{e.stopPropagation();setSelectedAnnotation(ann.id===selectedAnnotation?null:ann.id);}}
                        style={{position:'absolute',left:ann.x+'%',top:ann.y+'%',transform:'translate(-50%,-50%)',zIndex:10,cursor:'pointer'}}>
                        {ann.type==='text'&&(
                          <div style={{background:ann.id===selectedAnnotation?'#0073ea':'rgba(0,0,0,0.75)',color:'white',padding:'3px 8px',borderRadius:'4px',fontSize:'0.72rem',fontWeight:'bold',whiteSpace:'nowrap',boxShadow:'0 2px 6px rgba(0,0,0,0.2)',border:ann.id===selectedAnnotation?'2px solid white':'2px solid transparent'}}>
                            {ann.text}
                          </div>
                        )}
                        {ann.type==='callout'&&(
                          <div style={{background:'white',border:'2px solid '+(ann.id===selectedAnnotation?'#0073ea':'#e2445c'),borderRadius:'6px',padding:'4px 8px',fontSize:'0.72rem',fontWeight:'bold',maxWidth:'120px',textAlign:'center',boxShadow:'0 2px 6px rgba(0,0,0,0.15)'}}>
                            <div style={{width:6,height:6,background:ann.id===selectedAnnotation?'#0073ea':'#e2445c',borderRadius:'50%',margin:'0 auto 3px'}}/>
                            {ann.text}
                          </div>
                        )}
                        {ann.type==='arrow'&&(
                          <div style={{position:'relative'}}>
                            <div style={{position:'absolute',top:0,left:0,width:0,height:0,borderLeft:'6px solid transparent',borderRight:'6px solid transparent',borderBottom:'10px solid '+(ann.id===selectedAnnotation?'#0073ea':'#e2445c'),transform:'rotate(-45deg)'}}/>
                            <div style={{position:'absolute',top:12,left:8,background:ann.id===selectedAnnotation?'#0073ea':'rgba(0,0,0,0.75)',color:'white',padding:'2px 6px',borderRadius:'3px',fontSize:'0.68rem',fontWeight:'bold',whiteSpace:'nowrap'}}>
                              {ann.text}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Edit selected annotation */}
                  {selectedAnnotation&&annotations.find(a=>a.id===selectedAnnotation&&a.view===view)&&(
                    <div style={{marginTop:'8px',padding:'8px',background:'#e8f0ff',borderRadius:'6px',display:'flex',gap:'8px',alignItems:'center'}}>
                      <label style={{fontSize:'0.7rem',color:'#0073ea',fontWeight:'bold'}}>Text:</label>
                      <input value={annotations.find(a=>a.id===selectedAnnotation)?.text||''} onChange={e=>updateAnnotation(selectedAnnotation,'text',e.target.value)} style={{flex:1,padding:'3px 6px',border:'1px solid #c5d9f5',borderRadius:'4px',fontSize:'0.78rem'}}/>
                      <label style={{fontSize:'0.7rem',color:'#0073ea',fontWeight:'bold'}}>Colour:</label>
                      <input type="color" value={annotations.find(a=>a.id===selectedAnnotation)?.color||'#e2445c'} onChange={e=>updateAnnotation(selectedAnnotation,'color',e.target.value)} style={{width:28,height:28,padding:0,border:'none',cursor:'pointer'}}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SPECS TAB ── */}
        {activeTab==='specs'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <div style={{fontWeight:'bold',fontSize:'0.9rem',color:'#323338'}}>Measurement Spec Sheet</div>
              <button onClick={addSpecRow} style={{padding:'5px 12px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #c5d9f5',borderRadius:'5px',cursor:'pointer',fontSize:'0.75rem',fontWeight:'bold'}}>+ Add Row</button>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
                <thead>
                  <tr style={{background:'#323338',color:'white'}}>
                    {['Point of Measure','S','M','L','XL','Tolerance','How to Measure',''].map(h=><th key={h} style={{padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap',fontWeight:'bold'}}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {specRows.map((r,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #e6e9ef',background:i%2===0?'white':'#f9fafb'}}>
                      <td style={{padding:'5px 6px'}}><input value={r.point} onChange={e=>updateSpec(i,'point',e.target.value)} style={{width:'130px',padding:'3px 6px',border:'1px solid #e6e9ef',borderRadius:'4px',fontSize:'0.78rem',fontWeight:'bold'}}/></td>
                      {['s','m','l','xl'].map(sz=><td key={sz} style={{padding:'5px 6px'}}><input value={r[sz]} onChange={e=>updateSpec(i,sz,e.target.value)} style={{width:'50px',padding:'3px 6px',border:'1px solid #e6e9ef',borderRadius:'4px',fontSize:'0.78rem',textAlign:'center'}}/></td>)}
                      <td style={{padding:'5px 6px'}}><input value={r.tol} onChange={e=>updateSpec(i,'tol',e.target.value)} style={{width:'55px',padding:'3px 6px',border:'1px solid #e6e9ef',borderRadius:'4px',fontSize:'0.78rem',textAlign:'center'}}/></td>
                      <td style={{padding:'5px 6px'}}><input value={r.how} onChange={e=>updateSpec(i,'how',e.target.value)} style={{width:'200px',padding:'3px 6px',border:'1px solid #e6e9ef',borderRadius:'4px',fontSize:'0.78rem'}}/></td>
                      <td style={{padding:'5px 6px'}}><button onClick={()=>removeSpec(i)} style={{background:'none',border:'none',cursor:'pointer',color:'#e2445c',fontSize:'0.8rem',padding:'2px'}}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{marginTop:'14px',padding:'10px',background:'#f0fdf4',borderRadius:'6px',fontSize:'0.75rem',color:'#166534',border:'1px solid #86efac'}}>
              💡 Sizes S / M / L / XL. All measurements in cm. Tolerance = acceptable deviation. Grade up by 4cm for each size typically.
            </div>
          </div>
        )}

        {/* ── BOM TAB ── */}
        {activeTab==='bom'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <div style={{fontWeight:'bold',fontSize:'0.9rem',color:'#323338'}}>Bill of Materials</div>
              <button onClick={addBomRow} style={{padding:'5px 12px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #c5d9f5',borderRadius:'5px',cursor:'pointer',fontSize:'0.75rem',fontWeight:'bold'}}>+ Add Item</button>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
                <thead>
                  <tr style={{background:'#323338',color:'white'}}>
                    {['Item','Description','Supplier','Colour','Composition','GSM','UOM','Consumption','Rate',''].map(h=><th key={h} style={{padding:'8px 8px',textAlign:'left',whiteSpace:'nowrap',fontWeight:'bold',fontSize:'0.72rem'}}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {bomRows.map((r,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #e6e9ef',background:i%2===0?'white':'#f9fafb'}}>
                      {[['item',100,'bold'],['desc',130],['supplier',100],['colour',80],['composition',100],['gsm',50],['uom',55],['consumption',80],['rate',70]].map(([k,w,fw])=>(
                        <td key={k} style={{padding:'4px 5px'}}>
                          <input value={r[k]} onChange={e=>updateBom(i,k,e.target.value)} style={{width:w+'px',padding:'3px 5px',border:'1px solid #e6e9ef',borderRadius:'4px',fontSize:'0.75rem',fontWeight:fw||'normal'}}/>
                        </td>
                      ))}
                      <td style={{padding:'4px 5px'}}><button onClick={()=>removeBom(i)} style={{background:'none',border:'none',cursor:'pointer',color:'#e2445c',fontSize:'0.8rem',padding:'2px'}}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{marginTop:'14px',display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[['Total Items',bomRows.length],['Fabric Items',bomRows.filter(r=>r.uom==='m'||r.uom==='yard').length],['Trim Items',bomRows.filter(r=>r.uom==='pcs'||r.uom==='cones').length]].map(([k,v])=>(
                <div key={k} style={{background:'#f5f6f8',borderRadius:'6px',padding:'10px',textAlign:'center'}}>
                  <div style={{fontSize:'0.62rem',color:'#676879',fontWeight:'bold',textTransform:'uppercase'}}>{k}</div>
                  <div style={{fontSize:'1.1rem',fontWeight:'bold',color:'#323338'}}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// -----------------------------------------------------------------------------
//  MAIN WORKSTATION COMPONENT
// -----------------------------------------------------------------------------
export default function Workstation({ db, setDb, saveToDB, onOpenMarkerMaker }) {
  // Use parent's saveToDB if provided (consistent with rest of app), else fall back to internal
  const doSave = saveToDB || ((cat, data) => saveToAPI(cat, 'STYLE', data));
  const [selectedStyle, setSelectedStyle] = useState(null);
  // Cascading style selector
  const [hBrand, setHBrand] = useState('');
  const [hSeason, setHSeason] = useState('');
  const [hFabric, setHFabric] = useState('');
  const [hProduct, setHProduct] = useState('');
  const [wsData, setWsData] = useState({ items: [], globalTexture: null });
  const [history, setHistory] = useState([]); // Array to store previous items states for Undo
  const [selIds, setSelIds] = useState([]);
  const [sidebarTab, setSidebarTab] = useState('parts');
  const [isTextureMode, setIsTextureMode] = useState(false);
  const canvasContainerRef = useRef(null);
  const [obSequence, setObSequence] = useState([]);
  const [globalTargetEff, setGlobalTargetEff] = useState(75); // Editable Target Efficiency
  const [globalShiftHours, setGlobalShiftHours] = useState(8); // Editable Working Hours/day — drives Daily Target
  
  const [isGenerating3D, setIsGenerating3D] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [showFullOB, setShowFullOB] = useState(false);
  const [showOBImport, setShowOBImport] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canvasView, setCanvasView] = useState('front'); 
  const [savedFront, setSavedFront] = useState(null);
  const [savedBack, setSavedBack] = useState(null);
  const [bgThreshold, setBgThreshold] = useState(240);
  const [partSearch, setPartSearch] = useState('');
  const [partFilterFabric, setPartFilterFabric] = useState('');
  const [partFilterGarment, setPartFilterGarment] = useState('');
  const [partFilterDesign, setPartFilterDesign] = useState('');
  const [imageSearchUrl, setImageSearchUrl] = useState(null);
  const [imageSearchScores, setImageSearchScores] = useState({});
  const [isImageSearching, setIsImageSearching] = useState(false);
  const [hoverPart, setHoverPart] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const hoverTimer = useRef(null);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [showTechpack, setShowTechpack] = useState(false);
  const [smartMatch, setSmartMatch]   = useState(null); // { image, feats } when triggered
  const [smartMatchOpen, setSmartMatchOpen] = useState(false);

  // 1. Ensure definitions
  const allParts = db.masterParts || db.parts || []; 
  const allTrims = (db.sourcingProducts || []).filter(p => p.category === 'Trims' || p.type === 'Trim');
  const vaItems = (db.vaLibrary || []).filter(v => v.process === 'Print' || v.process === 'Embroidery');

  useEffect(() => {
    if (selectedStyle) {
      const wd = selectedStyle.workstationData || {};
      setWsData({ items: [], globalTexture: null, ...wd });
      setObSequence(wd.obSequence || []);
      setSavedFront(wd.frontImage || null);
      setSavedBack(wd.backImage || null);
      setGlobalTargetEff(selectedStyle.obSettings?.eff || 75);
      setGlobalShiftHours(selectedStyle.obSettings?.shiftHours || 8);
      setHistory([]); // Reset history on new style
    }
  }, [selectedStyle]);

  // Sync Parts to OB
  useEffect(() => {
    const allProcs = wsData.items.flatMap(item => (item.processes || []).map(p => ({
        ...p, partName: item.name, partId: item.instId, partPerimeter: 2 * ((parseFloat(item.lSpec) || 0) + (parseFloat(item.wSpec) || 0)),
        partArea: (parseFloat(item.lSpec) || 0) * (parseFloat(item.wSpec) || 0) * (parseFloat(item.layers) || 1), seqId: p.seqId || `${item.instId}_${Math.random()}`,
        // Auto-link the canvas part's own image so it shows in the OB without
        // a separate manual upload. Only applied to brand-new rows below —
        // any row already in obSequence (with a possible manual override
        // from the OB edit panel) is preserved untouched by the merge logic.
        partImage: item.image || p.partImage || ''
      }))
    );
    const existing = obSequence.filter(s => allProcs.some(ap => ap.partId === s.partId && ap.name === s.name));
    const newProcs = allProcs.filter(ap => !existing.some(e => e.partId === ap.partId && e.name === ap.name));
    if (newProcs.length > 0 || existing.length !== obSequence.length) { setObSequence([...existing, ...newProcs]); }
  }, [wsData.items]);

  // Helper to save state for Undo (Ctrl+Z)
  const saveHistory = useCallback((currentItems) => {
    setHistory(prev => [...prev, currentItems].slice(-30)); // Keep last 30 states
  }, []);

  const handleUndo = useCallback(() => {
    setHistory(prev => {
      if (prev.length === 0) return prev;
      const previousItems = prev[prev.length - 1];
      setWsData(curr => ({ ...curr, items: previousItems }));
      return prev.slice(0, -1);
    });
  }, []);

  // Keyboard Shortcuts (Arrows & Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { handleUndo(); return; }
      
      if (selIds.length > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return; // Don't move if typing in an input
        e.preventDefault();
        if (!e.repeat) saveHistory(wsData.items); // Only save history on initial press, not hold
        
        const step = e.shiftKey ? 10 : 1;
        let dx = 0, dy = 0;
        if (e.key === 'ArrowUp') dy = -step; if (e.key === 'ArrowDown') dy = step;
        if (e.key === 'ArrowLeft') dx = -step; if (e.key === 'ArrowRight') dx = step;
        
        setWsData(p => ({ ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, x: i.x + dx, y: i.y + dy } : i) }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selIds, wsData.items, handleUndo, saveHistory]);

  const handleImageSearch = async (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    setIsImageSearching(true);
    setImageSearchUrl(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const sf = await analyzeImageColor(ev.target.result);
      const scores = {};
      for(const p of allParts) if(p.image) scores[p.id]=colorSimilarity(sf, await analyzeImageColor(p.image));
      setImageSearchScores(scores);
      setIsImageSearching(false);
    };
    reader.readAsDataURL(file);
  };

  const addPart = (part) => {
    if (!selectedStyle) return alert('Select a Style first');
    saveHistory(wsData.items);
    const newItem = {
      ...part, instId: Date.now(), x: 80 + wsData.items.length * 15, y: 80 + wsData.items.length * 15, w: 200, h: 200, rotate: 0, lSpec: part.dnaL || 20, wSpec: part.dnaW || 15, layers: 1,
      textureScale: 100, textureRotation: 0, sketchOpacity: 25, textureOpacity: 100, flipH: false, flipV: false, processes: (part.processes || []).map(p => ({ ...p, seqId: Math.random() }))
    };
    setWsData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  // MULTIPLE SELECTION AND DRAG
  const handlePartMouseDown = (e, instId) => {
    e.stopPropagation();
    let newSel = [...selIds];
    if (e.shiftKey || e.ctrlKey) {
      if (newSel.includes(instId)) newSel = newSel.filter(id => id !== instId);
      else newSel.push(instId);
    } else {
      if (!newSel.includes(instId)) newSel = [instId];
    }
    setSelIds(newSel);

    saveHistory(wsData.items);
    const startX = e.clientX, startY = e.clientY;
    
    // Freeze starting positions for all selected parts
    const startPositions = wsData.items.reduce((acc, item) => {
      if (newSel.includes(item.instId)) { acc[item.instId] = { x: item.x, y: item.y, tx: item.textureOffsetX || 0, ty: item.textureOffsetY || 0 }; }
      return acc;
    }, {});

    const onMove = me => {
      const dx = me.clientX - startX, dy = me.clientY - startY;
      setWsData(p => ({ ...p, items: p.items.map(i => {
          if (!newSel.includes(i.instId)) return i;
          if (isTextureMode) return { ...i, textureOffsetX: startPositions[i.instId].tx + dx * 0.002, textureOffsetY: startPositions[i.instId].ty + dy * 0.002 };
          return { ...i, x: startPositions[i.instId].x + dx, y: startPositions[i.instId].y + dy };
      })}));
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };

  const handleResize = (e, instId) => {
    e.stopPropagation(); saveHistory(wsData.items);
    const startX = e.clientX, startY = e.clientY;
    const target = wsData.items.find(i => i.instId === instId);
    if (!target) return;
    const sw = target.w, sh = target.h;
    const onMove = me => setWsData(p => ({ ...p, items: p.items.map(i => i.instId === instId ? { ...i, w: Math.max(50, sw + me.clientX - startX), h: Math.max(50, sh + me.clientY - startY) } : i) }));
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };

  const handleTextureUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      saveHistory(wsData.items);
      if (selIds.length > 0) setWsData(p => ({ ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, localTexture: ev.target.result } : i) }));
      else setWsData(p => ({ ...p, globalTexture: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const importVATexture = (vaItem) => {
    if (selIds.length === 0) return alert('Select a part first');
    saveHistory(wsData.items);
    setWsData(p => ({ ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, localTexture: vaItem.image } : i) }));
  };

  const manipulatePart = (action, val) => {
    if (!selIds.length) return;
    saveHistory(wsData.items);
    setWsData(p => {
      if (action === 'front') { const sel = p.items.filter(i => selIds.includes(i.instId)), rest = p.items.filter(i => !selIds.includes(i.instId)); return { ...p, items: [...rest, ...sel] }; }
      if (action === 'back') { const sel = p.items.filter(i => selIds.includes(i.instId)), rest = p.items.filter(i => !selIds.includes(i.instId)); return { ...p, items: [...sel, ...rest] }; }
      if (action === 'flipH') return { ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, flipH: !i.flipH } : i) };
      if (action === 'flipV') return { ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, flipV: !i.flipV } : i) };
      return { ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, [action]: val } : i) };
    });
  };

  const removeBackground = async () => {
    if (!selIds.length) return alert('Select a part first');
    saveHistory(wsData.items);
    const part = wsData.items.find(i => selIds.includes(i.instId));
    if (!part?.image) return;
    const cleaned = await removeWhiteBackground(part.image, bgThreshold);
    setWsData(p => ({ ...p, items: p.items.map(i => selIds.includes(i.instId) ? { ...i, image: cleaned } : i) }));
  };

  const saveWorkstation = async () => {
    if (!selectedStyle || isSaving) return;
    setIsSaving(true);
    try {
      const styleImage = (await captureSmartImage(canvasContainerRef, wsData.items)) || selectedStyle.img;
      const aggregatedOps = obSequence.map(proc => ({
        procName: proc.name, partName: proc.partName,
        machine: (db.machines || []).find(m => proc.ops?.some(op => op.mcId == m.id))?.code || 'Man',
        smv: calcProcessSMV(proc, db).toFixed(4)
      }));

      // Build the full updated style (keep base64 img in local state for display)
      // Note: patterns are stored in the part_patterns DB table and loaded
      // lazily by Marker Maker via get_patterns.php — not bundled here.
      const updated = {
        ...selectedStyle, img: styleImage,
        workstationData: {
          ...wsData, obSequence, aggregatedOps, smv: totalSMV,
          frontImage: canvasView === 'front' ? styleImage : savedFront,
          backImage:  canvasView === 'back'  ? styleImage : savedBack
        },
        obSettings: { eff: globalTargetEff, shiftHours: globalShiftHours }
      };

      // Optimistic UI update
      setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === selectedStyle.id ? updated : s) }));

      // Save via the working endpoint - saveToAPI strips base64 internally
      await saveToAPI('style', 'STYLE', { ...updated, id: selectedStyle.id });

      // After saving, ask if user wants to proceed to Marker Maker
      if (onOpenMarkerMaker) {
        const go = window.confirm(
          '✓ Style saved successfully!\n\nWould you like to open this style in Marker Maker to begin pattern grading and lay planning?'
        );
        if (go) {
          onOpenMarkerMaker(updated);
          return;
        }
      } else {
        alert('✓ Workstation saved successfully!');
      }
    } catch (e) {
      alert('⚠ Save failed: ' + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Save OB linked to style
  const handleSaveOB = async (newSeq, eff, shiftHrs) => {
    if (!selectedStyle) return;
    const aggregatedOps = newSeq.map(proc => ({
      procName: proc.name, partName: proc.partName,
      machine: (db.machines || []).find(m => proc.ops?.some(op => op.mcId == m.id))?.code || 'Man',
      smv: calcProcessSMV(proc, db).toFixed(4),
      threadCm: calcProcessThread(proc, db).toFixed(1),
      threadTkt: proc.threadTkt || '', needleNo: proc.needleNo || '', partImage: proc.partImage || ''
    }));
    const totalThread = newSeq.reduce((s, p) => s + calcProcessThread(p, db), 0);
    const updated = {
      ...selectedStyle,
      workstationData: {
        ...wsData, obSequence: newSeq, aggregatedOps,
        smv: newSeq.reduce((s, p) => s + calcProcessSMV(p, db), 0)
      },
      obSettings: { eff, shiftHours: shiftHrs ?? globalShiftHours },
      totalThreadCm: totalThread
    };
    setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === selectedStyle.id ? updated : s) }));
    await saveToAPI('style', 'STYLE', { ...updated, id: selectedStyle.id });
    setObSequence(newSeq);
  };

  // Handle imported OB — merge imported processes into current obSequence.
  // Preserves any canvas-drawn processes not overlapping with imported ones.
  const handleOBImport = (importedProcs) => {
    setObSequence(prev => {
      const kept = prev.filter(existing =>
        !importedProcs.some(imp =>
          imp.name === existing.name && imp.partName === existing.partName
        )
      );
      return [...kept, ...importedProcs];
    });
  };


  const totalSMV = useMemo(() => obSequence.reduce((s, p) => s + calcProcessSMV(p, db), 0), [obSequence, db.machines, db.manualCodes]);
  const activePart = selIds.length === 1 ? wsData.items.find(i => i.instId === selIds[0]) : null;
  const uniqueFabrics = [...new Set(allParts.map(p=>p.fabricType).filter(Boolean))];
  const uniqueGarments = [...new Set(allParts.map(p=>p.garmentType).filter(Boolean))];
  const uniqueDesigns = [...new Set(allParts.map(p=>p.designAttr).filter(Boolean))];
  let filteredParts = allParts.filter(p => {
    const s = partSearch.toLowerCase();
    return (!s||(p.name||'').toLowerCase().includes(s)||(p.garmentType||'').toLowerCase().includes(s)||(p.designAttr||'').toLowerCase().includes(s))
      && (!partFilterFabric||p.fabricType===partFilterFabric)
      && (!partFilterGarment||p.garmentType===partFilterGarment)
      && (!partFilterDesign||p.designAttr===partFilterDesign);
  });
  if(imageSearchUrl && Object.keys(imageSearchScores).length>0){
    filteredParts=filteredParts.filter(p=>(imageSearchScores[p.id]||0)>35).sort((a,b)=>(imageSearchScores[b.id]||0)-(imageSearchScores[a.id]||0));
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f6f8' }}>
      
      {/* ── TOP BAR ─────────────────────────────────────────────────────────── */}
      <div style={{ padding: '8px 16px', background: 'white', borderBottom: '1px solid #e6e9ef', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, minWidth: 0, overflow: 'hidden' }}>

        {/* Title — always visible */}
        <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#323338', flexShrink: 0 }}>WORKSTATION</h3>

        {/* ── Style selector — all 5 dropdowns always in one row ── */}
        {!selectedStyle && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>

            {/* 1. Brand */}
            <select value={hBrand}
              onChange={e => { setHBrand(e.target.value); setHSeason(''); setHFabric(''); setHProduct(''); }}
              style={{ padding: '5px 8px', border: '1px solid #c5c7d4', borderRadius: 5, fontSize: '0.8rem', flex: '1 1 90px', minWidth: 0, maxWidth: 130 }}>
              <option value="">— Brand —</option>
              {[...new Set((db.mgmtStyles||[]).map(s=>s.brand).filter(Boolean))].sort().map(b=><option key={b} value={b}>{b}</option>)}
            </select>

            {/* 2. Season — active once Brand chosen */}
            <select value={hSeason} disabled={!hBrand}
              onChange={e => { setHSeason(e.target.value); setHFabric(''); setHProduct(''); }}
              style={{ padding: '5px 8px', border: '1px solid #c5c7d4', borderRadius: 5, fontSize: '0.8rem', flex: '1 1 90px', minWidth: 0, maxWidth: 120, opacity: hBrand ? 1 : 0.45 }}>
              <option value="">— Season —</option>
              {[...new Set((db.mgmtStyles||[]).filter(s=>s.brand===hBrand).map(s=>s.season).filter(Boolean))].sort().map(s=><option key={s} value={s}>{s}</option>)}
            </select>

            {/* 3. Fabric — active once Season chosen */}
            <select value={hFabric} disabled={!hSeason}
              onChange={e => { setHFabric(e.target.value); setHProduct(''); }}
              style={{ padding: '5px 8px', border: '1px solid #c5c7d4', borderRadius: 5, fontSize: '0.8rem', flex: '1 1 90px', minWidth: 0, maxWidth: 130, opacity: hSeason ? 1 : 0.45 }}>
              <option value="">— Fabric —</option>
              {[...new Set((db.mgmtStyles||[]).filter(s=>s.brand===hBrand&&s.season===hSeason).map(s=>getStyleFabric(s)).filter(Boolean))].sort().map(f=><option key={f} value={f}>{f}</option>)}
            </select>

            {/* 4. Product — active once Fabric chosen */}
            <select value={hProduct} disabled={!hFabric}
              onChange={e => setHProduct(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #c5c7d4', borderRadius: 5, fontSize: '0.8rem', flex: '1 1 90px', minWidth: 0, maxWidth: 130, opacity: hFabric ? 1 : 0.45 }}>
              <option value="">— Product —</option>
              {[...new Set((db.mgmtStyles||[]).filter(s=>s.brand===hBrand&&s.season===hSeason&&getStyleFabric(s)===hFabric).map(s=>getStyleProduct(s)).filter(Boolean))].sort().map(p=><option key={p} value={p}>{p}</option>)}
            </select>

            {/* 5. Style — active once Fabric chosen (product is optional) */}
            <select value="" disabled={!hFabric}
              onChange={e => { if (e.target.value) setSelectedStyle(db.mgmtStyles?.find(s=>s.id==e.target.value)); }}
              style={{ padding: '5px 8px', border: `1px solid ${hFabric ? '#0073ea' : '#c5c7d4'}`, borderRadius: 5, fontSize: '0.8rem', fontWeight: hFabric ? 'bold' : 'normal', background: hFabric ? '#e8f0ff' : 'white', color: hFabric ? '#1e40af' : '#9699a6', flex: '1 1 110px', minWidth: 0, maxWidth: 160, opacity: hFabric ? 1 : 0.45 }}>
              <option value="">— Style —</option>
              {(db.mgmtStyles||[]).filter(s=>
                s.brand===hBrand &&
                s.season===hSeason &&
                getStyleFabric(s)===hFabric &&
                (!hProduct||getStyleProduct(s)===hProduct)
              ).map(s=><option key={s.id} value={s.id}>{s.styleRef}</option>)}
            </select>

            {/* Import OB available even before style selection */}
            <button onClick={() => { setSidebarTab('ob'); setShowOBImport(true); }}
              style={{ padding: '5px 10px', background: '#7C3AED', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 'bold', flexShrink: 0, whiteSpace: 'nowrap' }}>
              [img] Import OB
            </button>
            {/* Smart Match — upload garment image to find closest style */}
            <label title="Upload a garment image to find the closest matching style" style={{ cursor:'pointer', flexShrink:0 }}>
              <div style={{ padding:'5px 12px', background: smartMatch ? '#059669' : '#0f172a', color:'white', borderRadius:5, fontSize:'0.78rem', fontWeight:'bold', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:5 }}>
                [search] Smart Match
              </div>
              <input type="file" accept="image/*" hidden onChange={async e => {
                const file = e.target.files?.[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = async ev => {
                  const img = ev.target.result;
                  const feats = await computeSimilarityFeatures(img);
                  setSmartMatch({ image: img, feats });
                  setSmartMatchOpen(true);
                };
                reader.readAsDataURL(file);
              }} />
            </label>
          </div>
        )}

        {/* ── Style selected — show style info + action buttons in one row ── */}
        {selectedStyle && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>

            {/* Back button */}
            <button onClick={() => setSelectedStyle(null)}
              style={{ padding: '5px 10px', background: '#f0f1f4', border: '1px solid #e6e9ef', borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}>← Back</button>

            {/* Style name */}
            <span style={{ fontWeight: 'bold', color: '#323338', fontSize: '0.9rem', flexShrink: 0 }}>{selectedStyle.styleRef}</span>

            {/* Style meta pills */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <span style={{ background: '#e3faf0', padding: '3px 8px', borderRadius: 6, color: '#166534', fontWeight: 'bold', fontSize: '0.75rem' }}>SMV: {totalSMV.toFixed(3)}</span>
              <span style={{ background: '#f0fdf4', padding: '3px 8px', borderRadius: 6, color: '#166534', fontSize: '0.75rem' }}>Qty: {(selectedStyle.totalQty||0).toLocaleString()}</span>
              {selectedStyle.brand && <span style={{ background: '#e8f0ff', padding: '3px 8px', borderRadius: 6, color: '#0073ea', fontSize: '0.75rem' }}>{selectedStyle.brand}</span>}
              {selectedStyle.season && <span style={{ background: '#fdf4ff', padding: '3px 8px', borderRadius: 6, color: '#a25ddc', fontSize: '0.75rem' }}>{selectedStyle.season}</span>}
            </div>

            {/* Plan Efficiency */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#e8f0ff', padding: '3px 10px', borderRadius: 6, color: '#1e40af', fontWeight: 'bold', fontSize: '0.78rem', flexShrink: 0 }}>
              Eff:
              <input type="number" value={globalTargetEff} onChange={e => setGlobalTargetEff(+e.target.value)}
                style={{ width: 46, border: 'none', background: 'transparent', borderBottom: '1px solid #93c5fd', fontWeight: 'bold', color: '#1e40af', textAlign: 'center', fontSize: '0.82rem' }} />%
            </span>

            {/* Working Hours — drives Daily Target. Was hardcoded to 8h before; now editable. */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff5e6', padding: '3px 10px', borderRadius: 6, color: '#92400e', fontWeight: 'bold', fontSize: '0.78rem', flexShrink: 0 }}>
              Shift:
              <input type="number" step="0.5" value={globalShiftHours} onChange={e => setGlobalShiftHours(+e.target.value)}
                style={{ width: 40, border: 'none', background: 'transparent', borderBottom: '1px solid #fdba74', fontWeight: 'bold', color: '#92400e', textAlign: 'center', fontSize: '0.82rem' }} />hrs
            </span>

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Action buttons — right side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <div style={{ display: 'flex', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
                {['front','back'].map(v => (
                  <button key={v} onClick={() => setCanvasView(v)}
                    style={{ padding: '5px 10px', background: canvasView===v ? '#0073ea' : 'white', color: canvasView===v ? 'white' : '#676879', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold', textTransform: 'uppercase' }}>{v}</button>
                ))}
              </div>
              <button onClick={saveWorkstation} disabled={isSaving}
                style={{ padding: '5px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', display: 'flex', gap: 4, alignItems: 'center', fontWeight: 'bold' }}>
                <Save size={13}/> {isSaving ? 'Saving…' : 'SAVE'}
              </button>
              <button onClick={() => { setSidebarTab('ob'); setShowOBImport(true); }}
                style={{ padding: '5px 12px', background: '#7C3AED', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', display: 'flex', gap: 4, alignItems: 'center', fontWeight: 'bold' }}>
                📥 Import OB
              </button>
              <button onClick={() => setIsGenerating3D(true)}
                style={{ padding: '5px 12px', background: '#0073ea', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', display: 'flex', gap: 4, alignItems: 'center' }}>
                <Box size={13}/> 3D
              </button>
              <button onClick={() => setShowTechpack(true)}
                style={{ padding: '5px 12px', background: '#7C3AED', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', display: 'flex', gap: 4, alignItems: 'center', fontWeight: 'bold' }}>
                📋 Techpack
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        
        {/* ── LEFT SIDEBAR ────────────────────────────────────────────────── */}
        <div style={{ width: '290px', background: 'white', borderRight: '1px solid #e6e9ef', display: 'flex', flexDirection: 'column', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', borderBottom: '1px solid #e6e9ef' }}>
            {[{ k: 'parts', label: 'Parts' }, { k: 'trims', label: 'Trims' }, { k: 'va', label: 'VA' }, { k: 'layout', label: 'Layout' }, { k: 'ob', label: 'OB' }].map(t => (
              <button key={t.k} onClick={() => setSidebarTab(t.k)} style={{ flex: 1, padding: '10px 4px', background: sidebarTab === t.k ? '#e8f0ff' : 'white', border: 'none', borderBottom: sidebarTab === t.k ? '2px solid #0073ea' : '2px solid transparent', fontWeight: 'bold', fontSize: '0.65rem', textTransform: 'uppercase', cursor: 'pointer', color: sidebarTab === t.k ? '#0073ea' : '#676879' }}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {sidebarTab === 'parts' && (
              <div>
                {/* Search + image search */}
                <div style={{display:'flex',alignItems:'center',gap:'5px',background:'#f0f1f4',borderRadius:'5px',padding:'5px 8px',marginBottom:'6px'}}>
                  {imageSearchUrl?<img src={imageSearchUrl} style={{width:18,height:18,borderRadius:3,objectFit:'cover'}} alt=""/>:<Search size={12} color="#9699a6"/>}
                  <input value={partSearch} onChange={e=>setPartSearch(e.target.value)} placeholder="Search name, garment, design…" style={{border:'none',background:'transparent',outline:'none',fontSize:'0.75rem',flex:1}}/>
                  <label title="Visual image search" style={{cursor:'pointer',display:'flex',alignItems:'center'}}>
                    {isImageSearching?<RefreshCw size={13} color="#0073ea"/>:<ImageIcon size={13} color={imageSearchUrl?'#0073ea':'#9699a6'}/>}
                    <input type="file" hidden accept="image/*" onChange={handleImageSearch}/>
                  </label>
                  {imageSearchUrl&&<button onClick={()=>{setImageSearchUrl(null);setImageSearchScores({});}} style={{background:'none',border:'none',cursor:'pointer',color:'#e2445c',lineHeight:1,padding:0}}>✕</button>}
                </div>
                {/* Filter dropdowns */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'4px',marginBottom:'8px'}}>
                  {[[uniqueFabrics,partFilterFabric,setPartFilterFabric,'Fabric'],[uniqueGarments,partFilterGarment,setPartFilterGarment,'Garment'],[uniqueDesigns,partFilterDesign,setPartFilterDesign,'Design']].map(([opts,val,setter,lbl])=>(
                    <select key={lbl} value={val} onChange={e=>setter(e.target.value)} style={{padding:'3px 4px',border:'1px solid #e6e9ef',borderRadius:'4px',fontSize:'0.65rem'}}>
                      <option value="">All {lbl}</option>
                      {opts.map(o=><option key={o} value={o}>{o}</option>)}
                    </select>
                  ))}
                </div>
                {imageSearchUrl&&<div style={{marginBottom:'6px',fontSize:'0.68rem',color:'#0073ea',background:'#e8f0ff',padding:'3px 8px',borderRadius:'4px'}}>Visual match: {filteredParts.length} parts</div>}
                {/* Parts grid */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                  {filteredParts.length===0&&<div style={{gridColumn:'1/-1',textAlign:'center',color:'#9699a6',fontSize:'0.8rem',padding:'20px 0'}}>No parts found.</div>}
                  {filteredParts.map(p=>(
                    <div key={p.id} onClick={()=>addPart(p)}
                      onMouseEnter={e=>{hoverTimer.current=setTimeout(()=>{setHoverPart(p);setHoverPos({x:e.clientX,y:e.clientY});},550);e.currentTarget.style.boxShadow='0 0 0 2px #0073ea';}}
                      onMouseLeave={e=>{clearTimeout(hoverTimer.current);setHoverPart(null);e.currentTarget.style.boxShadow='none';}}
                      onMouseMove={e=>setHoverPos({x:e.clientX,y:e.clientY})}
                      style={{border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer',textAlign:'center',background:'white',position:'relative',overflow:'hidden',transition:'box-shadow 0.12s'}}>
                      <div style={{height:55,background:'#f0f1f4',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
                        {p.image?<img src={p.image} style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}} alt={p.name}/>:<Layers size={16} color="#9699a6"/>}
                      </div>
                      <div style={{padding:'4px 5px 5px'}}>
                        <div style={{fontSize:'0.68rem',fontWeight:'bold',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                        <div style={{fontSize:'0.58rem',color:'#676879'}}>{p.garmentType||''}</div>
                        {imageSearchUrl&&imageSearchScores[p.id]&&<div style={{fontSize:'0.55rem',color:'#7C3AED'}}>{imageSearchScores[p.id].toFixed(0)}% match</div>}
                      </div>
                      <Plus size={11} style={{position:'absolute',top:4,right:4,color:'#0073ea'}}/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarTab === 'trims' && (
              <div>
                <div style={{fontSize:'0.7rem',color:'#676879',marginBottom:'8px',background:'#fff7ed',padding:'6px 8px',borderRadius:'5px',border:'1px solid #fde68a'}}>
                  Click a trim to <strong>add it to the canvas</strong> as a part.
                </div>
                {allTrims.length===0&&<div style={{textAlign:'center',color:'#9699a6',fontSize:'0.75rem',padding:'20px 0'}}>No Trims found in Sourcing module.<br/><span style={{fontSize:'0.65rem'}}>Add trim products in the Sourcing module.</span></div>}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                  {allTrims.map(t=>(
                    <div key={t.id} onClick={()=>addPart({...t,name:t.name||t.product,image:t.image})} title={'Add to canvas: '+(t.name||t.product)}
                      style={{border:'1px solid #fde68a',borderRadius:'6px',cursor:'pointer',textAlign:'center',position:'relative',overflow:'hidden',background:'#fffbeb'}}
                      onMouseEnter={e=>e.currentTarget.style.boxShadow='0 0 0 2px #f59e0b'}
                      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                      <div style={{height:55,background:'#fef3c7',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
                        {t.image?<img src={t.image} style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}} alt={t.name}/>:<Scissors size={14} color="#d97706"/>}
                      </div>
                      <div style={{padding:'4px 5px 5px'}}>
                        <div style={{fontSize:'0.68rem',fontWeight:'bold',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.name||t.product}</div>
                        <div style={{fontSize:'0.6rem',color:'#92400e'}}>{t.supplier||t.supplierName||''}</div>
                        {t.rate&&<div style={{fontSize:'0.58rem',color:'#059669',fontWeight:'bold'}}>${t.rate}</div>}
                      </div>
                      <Plus size={11} style={{position:'absolute',top:4,right:4,color:'#d97706'}}/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarTab === 'va' && (
              <div>
                <div style={{fontSize:'0.7rem',color:'#676879',marginBottom:'8px',background:'#fdf4ff',padding:'6px 8px',borderRadius:'5px',border:'1px solid #e9d5ff'}}>
                  <strong>Select a part on canvas</strong> first, then click a VA item to apply it as texture.
                </div>
                {vaItems.length===0&&<div style={{textAlign:'center',color:'#9699a6',fontSize:'0.75rem',padding:'20px 0'}}>No Print/Embroidery items in VA Library.<br/><span style={{fontSize:'0.65rem'}}>Add items in the VA Library module first.</span></div>}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                  {vaItems.map(v=>(
                    <div key={v.id} onClick={()=>importVATexture(v)} title={'Click to apply to selected part: '+v.name}
                      style={{border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer',overflow:'hidden'}}
                      onMouseEnter={e=>e.currentTarget.style.boxShadow='0 0 0 2px #a25ddc'}
                      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                      <div style={{height:60,background:'#f5f6f8',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
                        {v.image?<img src={v.image} style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}} alt={v.name}/>:<Palette size={14} color="#9699a6"/>}
                      </div>
                      <div style={{padding:'5px 6px',fontSize:'0.65rem'}}>
                        <div style={{fontWeight:'bold',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{v.name}</div>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'2px'}}>
                          <span style={{color:'#a25ddc',fontSize:'0.6rem'}}>{v.process}</span>
                          {v.rate&&<span style={{color:'#059669',fontSize:'0.6rem',fontWeight:'bold'}}>${v.rate}/{v.uom||'m'}</span>}
                        </div>
                        {v.supplier&&<div style={{color:'#9699a6',fontSize:'0.58rem',marginTop:'1px'}}>{v.supplier}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarTab === 'layout' && (
              <div>
                <button onClick={()=>setShowLayoutModal(true)}
                  style={{width:'100%',marginBottom:'10px',padding:'9px',background:'#323338',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'0.78rem',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',fontWeight:'bold'}}>
                  <Maximize size={13}/> FULL SCREEN LAYOUT
                </button>
                <LayoutChart obSequence={obSequence} db={db} compact={true}/>
              </div>
            )}

            {sidebarTab === 'ob' && (
              <div>
                {/* Import OB — works with or without a style selected */}
                <button onClick={() => setShowOBImport(true)} style={{ width: '100%', marginBottom: '8px', padding: '11px', background: '#7C3AED', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold', boxShadow: '0 2px 8px #7C3AED44' }}>
                  📥 IMPORT OB FROM EXCEL / CSV
                </button>
                {!selectedStyle && (
                  <div style={{ marginBottom: 10, padding: '10px 12px', background: '#FFF7ED', border: '1px solid #FDE68A', borderRadius: 6, fontSize: 12, color: '#92400E' }}>
                    💡 Select a style first, then import an OB to link it to that style. Or import now and save to link later.
                  </div>
                )}
                <button onClick={() => setShowFullOB(true)} style={{ width: '100%', marginBottom: '10px', padding: '9px', background: '#0073ea', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontWeight:'bold' }}><Maximize size={13} /> MAXIMIZE OB</button>
                {/* SMV + Efficiency-driven targets summary */}
                <div style={{ marginBottom: '8px', padding: '10px', background: '#f5f6f8', borderRadius: '6px', fontSize: '0.75rem', border:'1px solid #e6e9ef' }}>
                  {(() => {
                    // Single source of truth — same buildOBData used by the
                    // maximize screen, so these numbers always agree.
                    const ob = buildOBData(obSequence, db, selectedStyle, globalTargetEff, globalShiftHours * 60);
                    return (
                      <>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Total SMV</span><strong style={{color:'#059669'}}>{ob.totalSMV.toFixed(3)} min</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Processes</span><strong>{obSequence.length}</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Pitch Time</span><strong>{ob.pitchTime.toFixed(3)} min</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Plan Eff</span><strong style={{color:'#1e40af'}}>{globalTargetEff}%</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Working Hours</span><strong style={{color:'#92400e'}}>{globalShiftHours}h</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Workforce Needed</span><strong style={{color:'#7c3aed'}}>{ob.workforceNeeded} workers</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{color:'#676879'}}>Machines</span><strong style={{color:'#0369a1'}}>{ob.uniqueMachines} types</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px',background:'#fff5e6',padding:'3px 5px',borderRadius:'3px'}}><span style={{color:'#92400e',fontWeight:'bold'}}>Hourly Target</span><strong style={{color:'#b45309'}}>{ob.rows.length ? parseFloat(((60/ob.totalSMV)*(globalTargetEff/100)).toFixed(1)) : 0} pcs/hr</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px',background:'#e3faf0',padding:'3px 5px',borderRadius:'3px'}}><span style={{color:'#166534',fontWeight:'bold'}}>Daily Target ({globalShiftHours}h)</span><strong style={{color:'#166534'}}>{ob.dailyTarget} pcs</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'#676879'}}>Total Thread</span><strong style={{color:'#a25ddc'}}>{ob.totalThreadCm.toFixed(1)} cm</strong></div>
                      </>
                    );
                  })()}
                </div>
                {/* Part Geometry from Canvas items */}
                {wsData.items.length > 0 && (
                  <div style={{ padding: '10px', background: '#f0fdf4', borderRadius: '6px', fontSize: '0.72rem', border:'1px solid #86efac' }}>
                    <div style={{ fontWeight:'bold', color:'#166534', marginBottom:'6px', fontSize:'0.73rem' }}>📐 PART GEOMETRY</div>
                    {wsData.items.map((item, idx) => {
                      const L = parseFloat(item.lSpec)||0, W = parseFloat(item.wSpec)||0;
                      const g = item.geometry || null;
                      const area = g ? g.area : (L*W).toFixed(1);
                      const perim = g ? g.perimeter : (2*(L+W)).toFixed(1);
                      const shape = g ? g.shape : 'Rectangle';
                      return (
                        <div key={idx} style={{ marginBottom:'6px', paddingBottom:'6px', borderBottom: idx < wsData.items.length-1 ? '1px solid #b3f5d6' : 'none' }}>
                          <div style={{ fontWeight:'bold', color:'#323338', marginBottom:'2px' }}>{item.name}</div>
                          <div style={{ display:'flex', gap:'8px', color:'#676879' }}>
                            <span>Area: <strong>{area} cm²</strong></span>
                            <span>Perim: <strong>{perim} cm</strong></span>
                          </div>
                          <div style={{ color:'#676879', fontSize:'0.68rem' }}>Shape: {shape} {g?.confidence ? `(${g.confidence})` : '(estimated)'}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── CANVAS ──────────────────────────────────────────────────────── */}
        <div ref={canvasContainerRef} style={{ flex: 1, position: 'relative', background: '#e6e9ef', overflow: 'hidden', cursor: isTextureMode ? 'crosshair' : 'default' }}>
          <div style={{ position: 'absolute', inset: 0 }} onClick={() => setSelIds([])} />
          <ThreeFabricLayer items={wsData.items} globalTextureUrl={wsData.globalTexture} parentRef={canvasContainerRef} />
          
          <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(255,255,255,0.85)', borderRadius: '6px', padding: '3px 10px', fontSize: '0.72rem', fontWeight: 'bold', color: '#323338', pointerEvents: 'none', zIndex: 20 }}>{canvasView.toUpperCase()} VIEW</div>
          <div style={{ position: 'absolute', top: 10, left: 120, background: 'rgba(255,255,255,0.85)', borderRadius: '6px', padding: '3px 10px', fontSize: '0.65rem', color: '#676879', pointerEvents: 'none', zIndex: 20 }}>Shift+Click to Multi-Select | Arrows to Nudge | Ctrl+Z to Undo</div>

          {savedFront && <img src={savedFront} style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, width: 60, height: 60, objectFit: 'contain', background: 'white', borderRadius: '6px', padding: '4px' }} alt="front" />}
          {savedBack && <img src={savedBack} style={{ position: 'absolute', top: savedFront ? 95 : 10, right: 10, zIndex: 20, width: 60, height: 60, objectFit: 'contain', background: 'white', borderRadius: '6px', padding: '4px' }} alt="back" />}

          {[...wsData.items].map((p, layerIdx) => {
            const isSelected = selIds.includes(p.instId);
            return (
              <div key={p.instId} style={{ position: 'absolute', left: p.x, top: p.y, width: p.w, height: p.h, transform: `rotate(${p.rotate || 0}deg) scaleX(${p.flipH ? -1 : 1}) scaleY(${p.flipV ? -1 : 1})`, border: isSelected ? '2px solid #0073ea' : 'none', cursor: isTextureMode ? 'crosshair' : 'move', zIndex: 10 + layerIdx }} onMouseDown={e => handlePartMouseDown(e, p.instId)} onClick={e => e.stopPropagation()}>
                <div style={{ width: '100%', height: '100%', backgroundImage: `url(${p.image})`, backgroundSize: '100% 100%', opacity: (p.sketchOpacity ?? 25) / 100 }} />
                {!isTextureMode && isSelected && selIds.length === 1 && <div style={{ width: 12, height: 12, background: '#0073ea', borderRadius: '50%', position: 'absolute', bottom: -6, right: -6, cursor: 'nwse-resize', border: '2px solid white', zIndex: 20 }} onMouseDown={e => handleResize(e, p.instId)} />}
              </div>
            );
          })}
          
          {isTextureMode && (
            <div style={{ position: 'absolute', bottom: 15, left: '50%', transform: 'translateX(-50%)', background: '#fdab3d', color: 'white', padding: '5px 15px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', pointerEvents: 'none', zIndex: 30 }}>TEXTURE OFFSET MODE — Drag to shift texture position</div>
          )}
        </div>

        {/* ── RIGHT PROPERTIES PANEL ───────────────────────────────────────── */}
        <div style={{ width: '275px', background: 'white', borderLeft: '1px solid #e6e9ef', display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '12px 14px', fontWeight: 'bold', color: '#323338', borderBottom: '1px solid #e6e9ef', fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>PROPERTIES</span>
            {selIds.length > 1 ? <span style={{ fontSize: '0.7rem', color: '#676879' }}>{selIds.length} Parts Selected</span> : (activePart && <span style={{ fontSize: '0.7rem', color: '#676879' }}>{activePart.name}</span>)}
          </div>

          {selIds.length > 0 ? (
            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* Dimensions (Only show if 1 item selected) */}
              {selIds.length === 1 && activePart && (
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#676879', textTransform: 'uppercase', marginBottom: '6px' }}>Dimensions</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px' }}>
                    {[['lSpec', 'L (cm)'], ['wSpec', 'W (cm)'], ['layers', 'Plies']].map(([k, label]) => (
                      <div key={k}>
                        <label style={{ fontSize: '0.65rem', color: '#9699a6' }}>{label}</label>
                        <input type="number" value={activePart[k] || ''} onChange={e => manipulatePart(k, e.target.value)} style={{ width: '100%', padding: '4px 5px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.8rem' }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Visuals */}
              {selIds.length === 1 && activePart && (
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#676879', textTransform: 'uppercase', marginBottom: '6px' }}>Visuals</div>
                  {[ ['rotate', 'Rotation', 0, 360], ['textureScale', 'Texture Scale', 20, 500], ['textureRotation', 'Texture Rotation', 0, 360], ['textureOpacity', 'Texture Opacity', 0, 100], ['sketchOpacity', 'Part Opacity', 0, 100] ].map(([k, label, min, max]) => (
                    <div key={k} style={{ marginBottom: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><label style={{ fontSize: '0.68rem', color: '#676879' }}>{label}</label><span style={{ fontSize: '0.68rem', color: '#9699a6' }}>{activePart[k] ?? (k === 'textureOpacity' ? 100 : k === 'sketchOpacity' ? 25 : 0)}</span></div>
                      <input type="range" min={min} max={max} value={activePart[k] ?? (k === 'textureOpacity' ? 100 : k === 'sketchOpacity' ? 25 : 0)} onChange={e => manipulatePart(k, +e.target.value)} style={{ width: '100%' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Manipulate */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#676879', textTransform: 'uppercase', marginBottom: '6px' }}>Manipulate</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginBottom: '5px' }}>
                  <button onClick={() => manipulatePart('front')} style={{ padding: '6px', background: '#f5f6f8', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}><BringToFront size={12} /> Bring Front</button>
                  <button onClick={() => manipulatePart('back')} style={{ padding: '6px', background: '#f5f6f8', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}><SendToBack size={12} /> Send Back</button>
                  <button onClick={() => manipulatePart('flipH')} style={{ padding: '6px', background: '#f5f6f8', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}><FlipHorizontal size={12} /> Flip H</button>
                  <button onClick={() => manipulatePart('flipV')} style={{ padding: '6px', background: '#f5f6f8', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}><FlipHorizontal size={12} style={{ transform: 'rotate(90deg)' }} /> Flip V</button>
                </div>

                {selIds.length === 1 && (
                  <div style={{ padding: '8px', background: '#fef9c3', borderRadius: '5px', marginBottom: '5px' }}>
                    <div style={{ fontSize: '0.68rem', color: '#92400e', fontWeight: 'bold', marginBottom: '4px' }}>Remove White Background</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                      <input type="range" min={150} max={255} value={bgThreshold} onChange={e => setBgThreshold(+e.target.value)} style={{ flex: 1 }} />
                    </div>
                    <button onClick={removeBackground} style={{ width: '100%', padding: '5px', background: '#fdab3d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem' }}>✂ Remove Background</button>
                  </div>
                )}
              </div>

              {/* UPLOAD TEXTURE FEATURE RESTORED HERE */}
              {selIds.length === 1 && activePart && (
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#676879', textTransform: 'uppercase', marginBottom: '6px' }}>Texture</div>
                  <button onClick={() => setIsTextureMode(m => !m)} style={{ width: '100%', padding: '7px', background: isTextureMode ? '#fff5e6' : '#f0f1f4', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem', marginBottom: '6px' }}>{isTextureMode ? '🟡 Exit Texture Offset' : '🔷 Enter Texture Offset'}</button>
                  
                  <label style={{ display: 'block', border: '1px dashed #c5c7d4', borderRadius: '5px', padding: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '0.72rem', color: '#676879' }}>
                    <ImageIcon size={15} style={{ display: 'block', margin: '0 auto 4px' }} />
                    Upload Custom Texture
                    <input type="file" hidden accept="image/*" onChange={handleTextureUpload} />
                  </label>
                  
                  {activePart.localTexture && (
                    <div style={{ marginTop: '6px', textAlign: 'center' }}>
                      <img src={activePart.localTexture} style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} alt="texture" />
                      <button onClick={() => manipulatePart('localTexture', null)} style={{ fontSize: '0.65rem', color: '#e2445c', background: 'none', border: 'none', cursor: 'pointer', marginTop: '2px' }}>✕ Remove Texture</button>
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => { 
                saveHistory(wsData.items); 
                setWsData(p => ({ ...p, items: p.items.filter(i => !selIds.includes(i.instId)) })); 
                setSelIds([]); 
              }} style={{ width: '100%', padding: '8px', background: '#fef2f2', color: '#e2445c', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>
                <Trash2 size={13} style={{ display: 'inline', marginRight: '4px' }} /> Delete Selected
              </button>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9699a6', fontSize: '0.82rem', padding: '20px', textAlign: 'center' }}><MousePointer2 size={28} color="#c5c7d4" style={{ marginBottom: '8px' }} /> Click a part to see properties</div>
          )}
        </div>
      </div>

      {showFullOB && <FullOBModal obSequence={obSequence} db={db} style={selectedStyle} targetEff={globalTargetEff} setTargetEff={setGlobalTargetEff} shiftHours={globalShiftHours} setShiftHours={setGlobalShiftHours} onClose={() => setShowFullOB(false)} onSeqChange={setObSequence} onSaveOB={handleSaveOB} />}
      {showOBImport && <OBImportModal db={db} onImport={handleOBImport} onClose={() => setShowOBImport(false)} />}
      {smartMatchOpen && smartMatch && (
        <StyleSmartMatchModal
          uploadedImage={smartMatch.image}
          uploadedFeats={smartMatch.feats}
          db={db}
          onSelectStyle={(style) => {
            if (style) {
              setSelectedStyle(style);
              setSmartMatch(null);
            }
          }}
          onClose={() => { setSmartMatchOpen(false); }}
        />
      )}

      {/* ── LAYOUT FULLSCREEN MODAL ── */}
      {showLayoutModal && ReactDOM.createPortal(
        <div style={{position:'fixed',inset:0,background:'white',zIndex:99999,display:'flex',flexDirection:'column'}}>
          <div style={{padding:'12px 20px',background:'#323338',color:'white',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
            <div>
              <h3 style={{margin:0,fontSize:'1rem'}}>PRODUCTION FLOOR LAYOUT</h3>
              <div style={{fontSize:'0.72rem',color:'#9699a6',marginTop:'2px'}}>{selectedStyle?.styleRef} — Assembly Line View</div>
            </div>
            <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
              <button onClick={()=>setShowLayoutModal(false)} style={{padding:'6px 14px',background:'#676879',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}>
                <ChevronLeft size={14}/> Back to Workstation
              </button>
            </div>
          </div>
          <div style={{flex:1,overflow:'auto',padding:'20px'}}>
            <LayoutChart obSequence={obSequence} db={db} compact={false}/>
          </div>
        </div>,
        document.body
      )}

      {/* ── TECHPACK MODAL ── */}
      {showTechpack && <TechpackModal style={selectedStyle} db={db} savedFront={savedFront} savedBack={savedBack} obSequence={obSequence} onClose={()=>setShowTechpack(false)} doSave={doSave}/>}

      {/* ── HOVER TOOLTIP ── */}
      {hoverPart && <PartHoverTooltip part={hoverPart} db={db} x={hoverPos.x} y={hoverPos.y}/>}
    </div>
  );
}